import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { getFunctions, httpsCallable } from 'firebase/functions'
import {
  collection,
  doc,
  setDoc,
  updateDoc,
  onSnapshot,
  serverTimestamp,
  deleteDoc,
  getDoc,
  arrayUnion,
  arrayRemove,
  getDocs,
  query,
  orderBy,
  startAfter,
  limit,
  where,
  runTransaction,
  deleteField
} from 'firebase/firestore'
import {
  Paperclip,
  Plus,
  ExternalLink,
  Send,
  Smile,
  Check,
  Reply,
  MoreVertical,
  X,
  Edit,
  Pin,
  Trash2,
  Info,
  XCircle,
  Settings,
  AlertTriangleIcon,
  CircleX,
  Image as ImageIcon,
  Video as VideoIcon,
  FileText
} from 'lucide-react'
import ModalDialog from '../ModalDialog'
import ChatUsersList from '../ChatUsersList'
import DateTime from '../DateTime'
import { useForm, useApiRequest } from '../../hooks'
import {
  auth,
  db,
  ENV_SUFFIX,
  orgScopedUsersQuery,
  storage
} from '../../firebase'
import {
  getDownloadURL,
  getMetadata,
  listAll,
  ref,
  uploadBytes,
  uploadBytesResumable
} from 'firebase/storage'
import toast from 'react-hot-toast'
import { useIncidentStatus } from '../../hooks/util'
import { useIdGenerator } from '../../hooks/useidgenerator'
import UserDetailsPanel from './UserDetailsPanel'
import PinnedMessagesList from './PinnedMessagesList'
import { useUserOnlineStatus } from '../../hooks/useOnlineStatus'
import { watchUserProfile } from '../../hooks/userProfiles'
import { getDisplayName, getUserAvatar } from '../../hooks/displayNameHelper'
import { sendNotificationToTopic } from '../../hooks/notificationService'
import { convertHEICToJPG } from '../../hooks/convertHeic'
import { useMapSettings } from '../Dispatch/DispatchMap'
import { useUserDisplayName } from '../../hooks/useUserDisplayName'
import { useHeicConverter } from '../../hooks/useHeicConverter'
// import { requestNotificationPermission } from "../../hooks/notification";

const sendChatNotification = async (notificationData) => {
  try {
    await sendNotificationToTopic(notificationData)
  } catch (error) {
    console.error('Error sending chat notification:', error)
    // Don't throw error to avoid breaking chat functionality
  }
}

// Send new message notification
const sendNewMessageNotification = async (
  chatId,
  message,
  senderName,
  channelData
) => {
  const notificationData = {
    topic: `${process.env.REACT_APP_ENV === 'production' ? 'p' : ''
      }livealarm-chat-${chatId}`,
    title: channelData?.groupName || 'Live Alram',
    body: `${senderName?.stringValue}: ${message.message.substring(0, 50)}${message.message.length > 50 ? '...' : ''
      }`,
    data: {
      type: 'chat_message',
      chatId: chatId,
      messageId: message.id || message.id
    }
  }

  await sendChatNotification(notificationData)
}

// Send message reaction notification
const sendReactionNotification = async (
  chatId,
  messageId,
  reactorName,
  reaction,
  recipientId
) => {
  const notificationData = {
    topic: `${process.env.REACT_APP_ENV === 'production' ? 'p' : ''
      }livealarm-chat-${chatId}-user-${recipientId}`,
    title: 'New Reaction',
    body: `${reactorName} reacted ${reaction} to your message`,
    data: {
      type: 'chat_message',
      chatId: chatId,
      messageId: messageId
    }
  }

  await sendChatNotification(notificationData)
}

// Send member added notification
const sendMemberAddedNotification = async (chatId, chatName, addedUserId) => {
  const notificationData = {
    topic: `${process.env.REACT_APP_ENV === 'production' ? 'p' : ''
      }livealarm-chat-common-${addedUserId}`,
    title: chatName || 'New Chat',
    body: `You were added to ${chatName || 'a group'}`,
    data: {
      type: 'chat_message',
      chatId: chatId
    }
  }

  await sendChatNotification(notificationData)
}

// Send admin status change notification
const sendAdminStatusNotification = async (chatId, chatName, userId, isAdd) => {
  const notificationData = {
    topic: `${process.env.REACT_APP_ENV === 'production' ? 'p' : ''
      }livealarm-chat-${chatId}-user-${userId}`,
    title: 'Admin Status Changed',
    body: isAdd
      ? `You are now an admin in ${chatName}`
      : `You are removed as an admin from ${chatName}`,
    data: {
      type: 'chat_message',
      chatId: chatId
    }
  }

  await sendChatNotification(notificationData)
}

// Send message pinned notification
const sendMessagePinnedNotification = async (chatId, chatName, pinnerName) => {
  const notificationData = {
    topic: `${process.env.REACT_APP_ENV === 'production' ? 'p' : ''
      }livealarm-chat-${chatId}`,
    title: chatName || 'Chat',
    body: `${pinnerName} pinned a message`,
    data: {
      type: 'chat_message',
      chatId: chatId
      // Note: messageId is optional for pin notifications per documentation
    }
  }

  await sendChatNotification(notificationData)
}

function UserDetailsModal({ user, onClose }) {
  const isOnline = useUserOnlineStatus(user?.userId)
  if (!user) return null
  return (
    <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4'>
      <div className='bg-white rounded-xl w-full max-w-md max-h-[90vh] overflow-y-auto'>
        <div className='p-6'>
          <div className='flex justify-between items-center mb-6'>
            <h2 className='text-xl font-bold text-gray-900'>User Details</h2>
            <button
              onClick={onClose}
              className='text-gray-400 hover:text-gray-600 cursor-pointer'>
              <X className='w-5 h-5' />
            </button>
          </div>
          <div className='flex flex-col items-center mb-6'>
            <div className='w-24 h-24 rounded-full bg-gray-200 mb-4 overflow-hidden'>
              <img
                src={getUserAvatar(user)}
                alt={user.displayName}
                className='w-full h-full object-cover'
              />
            </div>
            <h3 className='text-lg font-semibold text-gray-900'>
              {user.displayName}
            </h3>
            <p className='text-gray-500'>
              {user.email || 'No email available'}
            </p>
            <div
              className={`text-sm mt-1 ${isOnline ? 'text-green-500' : 'text-gray-500'
                }`}>
              {isOnline ? 'Online' : 'Offline'}
            </div>
          </div>
          <div className='space-y-4'>
            <div className='p-4 bg-gray-50 rounded-lg'>
              <h4 className='text-sm font-medium text-gray-500 mb-2'>
                User ID
              </h4>
              <p className='text-sm text-gray-900 break-all'>{user.userId}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function ChatAttachment({ media, title, url, timestamp }) {
  return (
    <div className='flex space-x-3 p-4 bg-gray-50 rounded-xl border border-gray-100 items-center'>
      <div className='flex-shrink-0'>
        <a href={url} target='_blank' rel='noreferrer' className='block'>
          {media || <Paperclip className='w-6 h-6 text-gray-400' />}
        </a>
      </div>
      <div className='flex-1 min-w-0'>
        <div className='mb-1'>
          <a
            href={url}
            target='_blank'
            rel='noreferrer'
            className='text-blue-600 hover:text-blue-800 font-medium text-sm'>
            {title}
          </a>
        </div>
        <div className='text-xs text-gray-400'>
          <DateTime value={timestamp} />
        </div>
      </div>
    </div>
  )
}

function ImageAttachment({ message, attachment }) {
  return (
    <ChatAttachment
      media={
        <img
          alt={attachment.fallback || 'Image'}
          className='w-12 h-12 object-cover rounded-lg'
          src={attachment.image_url || '/placeholder.svg'}
        />
      }
      title={attachment.title || attachment.fallback || 'Image'}
      url={attachment.image_url}
      timestamp={message.created_at}
    />
  )
}

function VideoAttachment({ message, attachment }) {
  return (
    <ChatAttachment
      media={
        <video className='w-12 h-12 object-cover rounded-lg'>
          <source src={attachment.asset_url} type={attachment.mime_type} />
        </video>
      }
      title={attachment.title || attachment.fallback || 'Video'}
      url={attachment.asset_url}
      timestamp={message.created_at}
    />
  )
}

function OtherAttachment({ message, attachment }) {
  return (
    <ChatAttachment
      title={attachment.title || attachment.fallback || 'File'}
      url={attachment.asset_url}
      timestamp={message.created_at}
    />
  )
}

function AttachmentsList({ channelId, onClose }) {
  const [attachments, setAttachments] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!channelId) return

    let isMounted = true // To prevent state updates after unmount
    setIsLoading(true)
    setError(null)

    const fetchAttachments = async () => {
      try {
        // Step 1: Get chatName from Firestore
        const chatDoc = await getDoc(doc(db, 'chats', channelId))
        const chatName = chatDoc.exists()
          ? chatDoc.data().name || channelId
          : channelId

        // Step 2: List all files under chats/{chatName}
        const folderRef = ref(storage, `stage/chats/${chatName}`)
        const result = await listAll(folderRef)

        const files = await Promise.all(
          result.items.map(async (itemRef) => {
            const url = await getDownloadURL(itemRef)
            const metadata = await getMetadata(itemRef)
            const fileType = metadata.contentType?.split('/')[0]
            return {
              message: {
                created_at: metadata.timeCreated
              },
              attachment: {
                type:
                  fileType === 'image'
                    ? 'image'
                    : fileType === 'video'
                      ? 'video'
                      : 'file',
                title: metadata.name,
                fallback: metadata.name,
                asset_url: url,
                mime_type: metadata.contentType,
                image_url: fileType === 'image' ? url : null
              }
            }
          })
        )

        if (isMounted) {
          setAttachments(files)
        }
      } catch (err) {
        console.error('Error loading chat attachments:', err)
        if (isMounted) {
          setError('Failed to load attachments')
        }
      } finally {
        if (isMounted) {
          setIsLoading(false)
        }
      }
    }

    fetchAttachments()

    return () => {
      isMounted = false
    }
  }, [channelId])

  return (
    <div className='px-3 py-3 space-y-4 bg-gray-50 h-screen overflow-y-auto'>
      <div className='flex justify-between items-center p-2'>
        <h2 className='text-lg font-semibold text-gray-900'>Attachments</h2>
        <button
          onClick={onClose}
          className='text-gray-400 hover:text-gray-600 cursor-pointer'>
          <X className='w-5 h-5' />
        </button>
      </div>
      {/* Loading state */}
      {isLoading && (
        <div className='flex justify-center items-center h-64'>
          <div className='animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500'></div>
        </div>
      )}

      {/* Error state */}
      {error && (
        <div className='text-center text-red-500 py-12'>
          <XCircle className='w-12 h-12 text-red-300 mx-auto mb-4' />
          <p>{error}</p>
          <button
            onClick={() => window.location.reload()}
            className='mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 cursor-pointer'>
            Retry
          </button>
        </div>
      )}

      {/* Empty state */}
      {!isLoading && !error && attachments.length === 0 && (
        <div className='text-center text-gray-500 py-12'>
          <Paperclip className='w-12 h-12 text-gray-300 mx-auto mb-4' />
          <p>No attachments added yet.</p>
        </div>
      )}

      {/* Success state */}
      {!isLoading && !error && attachments.length > 0 && (
        <div className='space-y-4'>
          {attachments.map((item, index) => {
            const { message, attachment } = item
            switch (attachment.type) {
              case 'image':
                return (
                  <ImageAttachment
                    key={`${index}-${attachment.asset_url}`}
                    message={message}
                    attachment={attachment}
                  />
                )
              case 'video':
                return (
                  <VideoAttachment
                    key={`${index}-${attachment.asset_url}`}
                    message={message}
                    attachment={attachment}
                  />
                )
              default:
                return (
                  <OtherAttachment
                    key={`${index}-${attachment.asset_url}`}
                    message={message}
                    attachment={attachment}
                  />
                )
            }
          })}
        </div>
      )}
    </div>
  )
}

function CustomChannelHeader({
  channelId,
  onEditChannel,
  onViewAttachments,
  onViewGroupDetails,
  onViewPinnedMessages,
  channelData,
  onCloseChat
}) {
  const [onlineStatuses, setOnlineStatuses] = useState({})
  const [typingStatus, setTypingStatus] = useState({})

  const displayName = useMemo(() => {
    if (!channelData) return 'Loading...'
    if (channelData.chatType === 'incident') {
      return channelData.groupName || 'Incident Chat'
    }
    if (channelData.chatType === 'p2p') {
      const otherMembers = channelData.members.filter(
        (m) => m.userId !== auth.currentUser?.uid
      )
      return otherMembers.map((m) => m.displayName).join(', ') || 'Direct Chat'
    }
    return channelData.groupName || 'Group Chat'
  }, [channelData])

  const memberCount = channelData?.members?.length || 0
  const members = channelData?.members || []

  useEffect(() => {
    if (!channelId) return

    const unsubscribe = onSnapshot(doc(db, 'chats', channelId), (doc) => {
      if (doc.exists()) {
        const data = doc.data()
        setTypingStatus(data.typingStatus || {})
      }
    })

    return () => unsubscribe()
  }, [channelId])

  useEffect(() => {
    if (!channelData?.members) return
    const unsubscribes = []
    const statuses = {}
    channelData.members.forEach((member) => {
      if (member.userId) {
        // Same as UserDetailsPanel: no error callback here meant a member from
        // an unbound organisation threw inside the SDK and never reported.
        unsubscribes.push(
          watchUserProfile(member.userId, (user) => {
            statuses[member.userId] = user?.isOnline || false
            setOnlineStatuses({ ...statuses })
          })
        )
      }
    })
    return () => unsubscribes.forEach((unsub) => unsub())
  }, [channelData?.members])

  const onlineCount = useMemo(() => {
    if (!channelData?.members) return 0
    return channelData.members.filter(
      (member) => member.userId && onlineStatuses[member.userId]
    ).length
  }, [channelData?.members, onlineStatuses])

  const typingUsers = useMemo(() => {
    if (!typingStatus) return []

    const currentUserId = auth.currentUser?.uid
    return Object.entries(typingStatus)
      .filter(([userId, status]) => userId !== currentUserId && status.isTyping)
      .map(([_, status]) => getDisplayName(status.name))
  }, [typingStatus])

  const subtitle = useMemo(() => {
    if (typingUsers.length > 0) {
      if (typingUsers.length === 1) {
        return `${typingUsers[0]} is typing...`
      }
      if (typingUsers.length === 2) {
        return `${typingUsers[0]} and ${typingUsers[1]} are typing...`
      }
      return `${typingUsers[0]} and ${typingUsers.length - 1
        } others are typing...`
    }

    return `${memberCount} members, ${onlineCount} online`
  }, [typingUsers, memberCount, onlineCount])

  return (
    <div className='flex items-center justify-between p-4 border-b border-gray-200 bg-white shadow-sm'>
      <div className='flex items-center space-x-3'>
        <div
          className={`w-10 h-10 rounded-full flex items-center justify-center shadow-sm
    ${channelData?.chatType === 'incident'
              ? 'bg-gray-200'
              : 'bg-gradient-to-br from-blue-500 to-blue-600'
            }`}>
          {channelData?.chatType === 'incident' ? (
            <AlertTriangleIcon className='text-gray-700 w-5 h-5' />
          ) : (
            <span className='text-white font-semibold text-base'>
              {displayName.charAt(0).toUpperCase()}
            </span>
          )}
        </div>
        <div>
          <h3 className='font-semibold text-gray-900'>{displayName}</h3>
          <p className='text-sm text-gray-500'>{subtitle}</p>
        </div>
      </div>
      <div className='flex items-center space-x-1 gap-2'>
        {channelData?.chatType !== 'p2p' && (
          <button
            onClick={onViewGroupDetails}
            className='p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-all duration-200 cursor-pointer'
            title='View attachments'>
            <Settings className='w-5 h-5' />
          </button>
        )}
        <button
          onClick={onViewAttachments}
          className='p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-all duration-200 cursor-pointer'
          title='View attachments'>
          <Paperclip className='w-5 h-5' />
        </button>
        <button
          onClick={onViewPinnedMessages}
          className='p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-all duration-200 cursor-pointer'
          title='View pinned messages'>
          <Pin className='w-5 h-5' />
        </button>
        {channelData?.chatType !== 'incident' &&
          channelData?.chatType !== 'group' && (
            <button
              onClick={onEditChannel}
              className='p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-all duration-200 cursor-pointer'
              title='Delete channel'>
              <Trash2 className='w-5 h-5' />
            </button>
          )}
        <CircleX
          className='text-gray-400 hover:text-gray-600 hover:bg-gray-100 pointer'
          style={{ cursor: 'pointer' }}
          // onClick={() => openSecondaryPanel(null)}
          onClick={onCloseChat}
        />
      </div>
    </div>
  )
}

function CreateChannelForm({
  onClose,
  profile,
  setCustomActiveChannel,
  setMessages,
  setInitialLoadComplete,
  setLastDocument,
  setNewestMessageTime,
  setHasMore,
  setShouldAutoScroll,
  setIsChatOpen,
  channels
}) {
  const { generateChatId } = useIdGenerator()
  const [getFieldProps, formData = {}, setFormData] = useForm({
    name: '',
    search: ''
  })
  const [selectedUserIds, setSelectedUserIds] = useState([])
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [visibleUsers, setVisibleUsers] = useState([])

  useEffect(() => {
    let unsubscribe = () => {}
    let cancelled = false
    ;(async () => {
      try {
        const q = await orgScopedUsersQuery()
        if (!q || cancelled) {
          setLoading(false)
          return
        }
        unsubscribe = onSnapshot(q, (snapshot) => {
          const usersData = snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data()
          }))
          setUsers(usersData)
          setVisibleUsers(
            usersData.filter((u) => u.id !== auth.currentUser?.uid)
          )
          setLoading(false)
        })
      } catch (error) {
        console.error('Error fetching users:', error)
        setLoading(false)
      }
    })()
    return () => {
      cancelled = true
      unsubscribe()
    }
  }, [])

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault()
      const { name } = formData
      const user = auth.currentUser
      if (!user) {
        console.error('No authenticated user')
        return
      }

      // Check if a P2P chat already exists with this user
      if (selectedUserIds.length === 1) {
        const targetUserId = selectedUserIds[0]
        const existingChat = channels.find(
          (chat) =>
            chat.chatType === 'p2p' &&
            chat.memberIds &&
            chat.memberIds.includes(targetUserId) &&
            chat.memberIds.includes(user.uid)
        )

        if (existingChat) {
          toast.success('Chat already exists with this user')
          onClose(existingChat.id)
          return
        }
      }

      const chatId = generateChatId()
      const members = [user.uid, ...selectedUserIds].map((uid) => {
        const userData = users.find((u) => u.id === uid) || {}
        return {
          userId: uid,
          displayName: userData.displayName || userData.name || `User_${uid}`,
          profilePic: getUserAvatar(userData),
          addedBy: uid !== user.uid ? user.uid : null,
          isOnline: userData.isOnline || false
        }
      })

      const memberIds = members.map((m) => m.userId)

      try {
        await setDoc(doc(db, 'chats', chatId), {
          chatType: selectedUserIds.length <= 1 ? 'p2p' : 'group',
          createdAt: new Date().toISOString(),
          createdBy: [user.uid],
          groupName:
            name || (selectedUserIds.length <= 1 ? null : 'Group Chat'),
          id: chatId,
          incidentId: '',
          lastMessage: null,
          lastMessageId: null,
          lastMessageSenderId: null,
          lastUpdatedAt: new Date().toISOString(),
          members,
          memberIds,
          mutedBy: [],
          pinnedMessages: [],
          profileBgColor: '#ffd99489',
          typingStatus: {},
          updateTimestamp: new Date().toISOString()
        })

        const allMemberIds = [user.uid, ...selectedUserIds]
        const membersToNotify = allMemberIds.filter(
          (memberId) => memberId !== user.uid
        )

        for (const memberId of membersToNotify) {
          try {
            await sendMemberAddedNotification(
              chatId,
              name || 'Group Chat',
              memberId
            )
          } catch (notificationError) {
            console.error(
              `Failed to send notification to user ${memberId}:`,
              notificationError
            )
          }
        }

        // Force update the active channel and reset message state
        setCustomActiveChannel(chatId)
        setMessages([])
        setInitialLoadComplete(false)
        setLastDocument(null)
        setNewestMessageTime(null)
        setHasMore(true)
        setShouldAutoScroll(true)
        setIsChatOpen(true)
        onClose(chatId)
      } catch (error) {
        console.error('Error creating chat:', error)
      }
    },
    [
      formData,
      onClose,
      selectedUserIds,
      profile,
      users,
      generateChatId,
      setCustomActiveChannel,
      setIsChatOpen,
      setMessages,
      setInitialLoadComplete,
      setLastDocument,
      setNewestMessageTime,
      setHasMore,
      setShouldAutoScroll
    ]
  )

  const handleUserSelect = (userId) => {
    setSelectedUserIds((prev) => {
      if (prev.includes(userId)) {
        return prev.filter((id) => id !== userId)
      } else {
        return [...prev, userId]
      }
    })
  }

  const handleSearch = (e) => {
    const term = e.target.value.toLowerCase()
    setSearchTerm(term)
    if (!term) {
      setVisibleUsers(users.filter((u) => u.id !== auth.currentUser?.uid))
      return
    }

    const filtered = users.filter((user) => {
      if (user.id === auth.currentUser?.uid) return false
      const displayName = Array.isArray(user.personalData)
        ? user.personalData.find((d) => d.type?.typeName === 'displayName')
          ?.value
        : ''
      return (
        user.name?.toLowerCase().includes(term) ||
        user.email?.toLowerCase().includes(term) ||
        displayName?.toLowerCase().includes(term)
      )
    })
    setVisibleUsers(filtered)
  }

  return (
    <div className='p-6'>
      <form onSubmit={handleSubmit} className='space-y-6'>
        <div>
          <label className='block text-sm font-medium text-gray-700 mb-2'>
            Chat Room Subject
          </label>
          <input
            type='text'
            name='name'
            placeholder='Enter chat room subject'
            required={selectedUserIds.length > 1}
            className='w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200'
            {...getFieldProps()}
          />
        </div>
        <div className='border border-gray-200 rounded-lg p-4'>
          <div className='mb-4'>
            <input
              type='text'
              name='search'
              placeholder='Search for users...'
              className='w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent'
              value={searchTerm}
              onChange={handleSearch}
            />
          </div>
          <h3 className='font-medium text-gray-900 mb-3'>Select Members</h3>
          {loading ? (
            <div className='flex justify-center py-8'>
              <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500'></div>
            </div>
          ) : (
            <div className='space-y-2 max-h-60 overflow-y-auto'>
              {visibleUsers.length === 0 ? (
                <div className='text-center py-8 text-gray-500'>
                  {searchTerm ? 'No users found' : 'No users available'}
                </div>
              ) : (
                visibleUsers.map((user) => (
                  <div
                    key={user.id}
                    className={`flex items-center p-3 rounded-lg cursor-pointer transition-all duration-200 ${selectedUserIds.includes(user.id)
                      ? 'bg-blue-50 border border-blue-200'
                      : 'hover:bg-gray-50 border border-transparent'
                      }`}
                    onClick={() => handleUserSelect(user.id)}>
                    <div className='w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center mr-3 overflow-hidden'>
                      <img
                        src={getUserAvatar(user)}
                        alt={user.displayName || user.name || 'User'}
                        className='w-full h-full object-cover'
                      />
                    </div>
                    <div className='flex-1 min-w-0'>
                      <div className='font-medium text-gray-900 truncate'>
                        {Array.isArray(user?.personalData)
                          ? user.personalData.find(
                            (item) => item.type?.typeName === 'displayName'
                          )?.value
                          : typeof user === 'string'
                            ? user // fallback: user is just a string name
                            : `User_${user?.id?.slice(0, 6)}`}
                      </div>
                      <div className='text-sm text-gray-500 truncate'>
                        {user.email || ''}
                      </div>
                    </div>
                    {selectedUserIds.includes(user.id) && (
                      <Check className='w-5 h-5 text-blue-500' />
                    )}
                  </div>
                ))
              )}
            </div>
          )}
        </div>
        <div className='flex justify-between items-center pt-4 border-t border-gray-200'>
          <div className='text-sm text-gray-500'>
            {selectedUserIds.length}{' '}
            {selectedUserIds.length === 1 ? 'member' : 'members'} selected
          </div>
          <div className='flex space-x-3'>
            <button
              type='button'
              onClick={() => onClose()}
              className='px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors cursor-pointer'>
              Cancel
            </button>
            <button
              type='submit'
              disabled={loading || selectedUserIds.length === 0}
              className='cursor-pointer px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200'>
              Create
            </button>
          </div>
        </div>
      </form>
    </div>
  )
}

function DeleteChannelForm({ channelId, onClose }) {
  const deleteChannel = useCallback(async () => {
    try {
      const functions = getFunctions()
      // The environment is the function, not a field in the request.
      const deleteChat = httpsCallable(functions, `deleteChat${ENV_SUFFIX}`)
      const result = await deleteChat({
        chatId: channelId
      })

      if (result.data.success) {
        toast.success('Chat channel deleted successfully')
        onClose()
      } else {
        throw new Error('Deletion failed on server')
      }
    } catch (error) {
      console.error('Error deleting channel:', error)
      toast.error('Failed to delete channel')
    }
  }, [channelId, onClose])

  return (
    <div className='p-6'>
      <div className='text-center space-y-6'>
        <div className='w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto'>
          <Trash2 className='w-8 h-8 text-red-500' />
        </div>
        <div>
          <h3 className='text-lg font-semibold text-gray-900'>
            Delete Channel
          </h3>
          <p className='text-gray-500 mt-2'>
            Are you sure you want to delete this channel? This action cannot be
            undone.
          </p>
        </div>
        <div className='flex justify-center space-x-3 pt-4'>
          <button
            type='button'
            onClick={onClose}
            className='px-6 py-2 text-gray-600 hover:text-gray-800 font-medium transition-colors cursor-pointer'>
            Cancel
          </button>
          <button
            type='button'
            onClick={deleteChannel}
            className='px-8 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 font-medium transition-all duration-200 shadow-sm cursor-pointer'>
            Delete
          </button>
        </div>
      </div>
    </div>
  )
}

function ChannelListContainer({
  channels,
  setActiveChannel,
  customActiveChannel,
  setIsChatOpen
}) {
  const getChannelName = (channel) => {
    if (channel.chatType === 'incident') {
      return channel.groupName || 'Incident Chat'
    }
    if (channel.chatType === 'p2p') {
      const otherMembers = channel.members.filter(
        (m) => m.userId !== auth.currentUser?.uid
      )
      return otherMembers.map((m) => m.displayName).join(', ') || 'Direct Chat'
    }
    return channel.groupName || 'Group Chat'
  }

  const sortedChannels = useMemo(() => {
    return [...channels].sort((a, b) => {
      // Convert ISO timestamps to milliseconds for comparison
      const getTime = (channel) => {
        if (!channel.lastUpdatedAt) return 0
        // Handle both Firestore Timestamp objects and ISO strings
        if (typeof channel.lastUpdatedAt === 'string') {
          return new Date(channel.lastUpdatedAt).getTime()
        }
        if (channel.lastUpdatedAt.seconds) {
          return channel.lastUpdatedAt.seconds * 1000
        }
        return 0
      }

      const aTime = getTime(a)
      const bTime = getTime(b)

      // If timestamps are equal, sort by channel name
      if (bTime === aTime) {
        const aName = getChannelName(a)
        const bName = getChannelName(b)
        return aName.localeCompare(bName)
      }

      return bTime - aTime // Sort descending (newest first)
    })
  }, [channels])

  const getChannelInitials = (channel) => {
    if (channel.chatType === 'incident') {
      return 'I'
    }
    if (channel.chatType === 'p2p') {
      const otherMembers = channel.members.filter(
        (m) => m.userId !== auth.currentUser?.uid
      )
      return otherMembers[0]?.displayName?.charAt(0)?.toUpperCase() || 'D'
    }
    return channel.groupName?.charAt(0)?.toUpperCase() || 'G'
  }

  const getAvatarColor = (channel, index) => {
    if (channel.chatType === 'incident') return 'from-red-500 to-red-600'
    if (index % 10 === 9) return 'from-yellow-500 to-yellow-600'
    return 'from-blue-500 to-blue-600'
  }

  const getAvatarText = (channel, index) => {
    if (channel.chatType === 'incident') return 'I'
    if (index % 10 === 9) return 'A17'
    return getChannelInitials(channel)
  }

  return (
    <div className='flex-1 overflow-y-auto'>
      {sortedChannels.map((channel, index) => (
        <div
          key={channel.id}
          className={`flex items-center space-x-3 p-2 cursor-pointer transition-all duration-200 ${channel.id === customActiveChannel
            ? 'bg-blue-50 border-r-4 border-blue-500'
            : 'hover:bg-gray-50'
            }`}
          onClick={() => {
            setActiveChannel(channel.id)
            // Add this line to open the chat
            setIsChatOpen(true)
          }}>
          <div
            className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 shadow-sm
    ${channel.chatType === 'incident'
                ? 'bg-gray-200'
                : `bg-gradient-to-br ${getAvatarColor(channel, index)}`
              }`}>
            {channel.chatType === 'incident' ? (
              // <AlertTriangleIcon
              //   className='w-5 h-5 text-white'
              //   fill='currentColor'
              //   stroke='none'
              // />
              <AlertTriangleIcon className='text-gray-700 w-5 h-5' />
            ) : (
              <span className='text-white font-semibold text-base'>
                {getAvatarText(channel, index)}
              </span>
            )}
          </div>

          <div className='flex-1 min-w-0'>
            <div className='text-gray-900 truncate'>
              {getChannelName(channel)}
            </div>
            <div className='text-xs text-gray-500 truncate'>
              {channel.lastMessage || 'Nothing yet... '}
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}

function ReactionPicker({ onSelect, onClose }) {
  const emojis = ['👍', '❤️', '😂', '😮', '😢', '😡']
  return (
    <div className='absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 bg-white border border-gray-200 rounded-full shadow-lg p-2 flex space-x-1 z-50'>
      {emojis.map((emoji) => (
        <button
          key={emoji}
          onClick={(e) => {
            e.stopPropagation()
            onSelect(emoji)
            onClose()
          }}
          className='w-8 h-8 flex items-center justify-center text-lg !bg-transparent cursor-pointer'>
          {emoji}
        </button>
      ))}
    </div>
  )
}

export function MessageItem({
  message,
  onReply,
  onAddReaction,
  onContextMenu,
  onViewProfile,
  showTimestamp = true,
  channelId
}) {
  const senderId = message?.senderId ?? null
  const safeChannelId = channelId ?? null
  const [{ showLastName }] = useMapSettings()
  const displayText = useUserDisplayName(senderId, showLastName, safeChannelId)
  const currentUserId = auth.currentUser?.uid
  const isDeletedForMe = message.deletedBy?.includes(currentUserId)
  const [showReactionPicker, setShowReactionPicker] = useState(false)
  const isOwnMessage = message.senderId === currentUserId

  const groupedReactions = useMemo(() => {
    if (!message.reactions || message.reactions.length === 0) return []

    const reactionMap = {}
    message.reactions.forEach((reaction) => {
      if (!reactionMap[reaction.reaction]) {
        reactionMap[reaction.reaction] = {
          emoji: reaction.reaction,
          count: 1,
          users: [reaction.userId],
          hasReacted: reaction.userId === currentUserId
        }
      } else {
        reactionMap[reaction.reaction].count += 1
        reactionMap[reaction.reaction].users.push(reaction.userId)
        if (reaction.userId === currentUserId) {
          reactionMap[reaction.reaction].hasReacted = true
        }
      }
    })

    return Object.values(reactionMap)
  }, [message.reactions, currentUserId])

  if (isDeletedForMe) return null
  const displayName = getDisplayName(message.senderName || message.displayName)
  const isPinMessage = message.messageType === 'pin' && message.pinData
  const isCompactPinMessage = isPinMessage && (message.message === '📷 Photo added to pin' || message.message === 'Photo added to pin')
  const messageText = (isCompactPinMessage ? '' : (message?.files?.caption || message?.message || ''))
  const hasAttachments = message.files?.attachments?.length > 0
  const hasContent = messageText.trim() !== '' || hasAttachments || isCompactPinMessage
  const videoFileTypes = [
    'mp4',
    'mov',
    'avi',
    'wmv',
    'flv',
    'mkv',
    'webm',
    'mpeg',
    'mpg',
    '3gp',
    'm4v'
  ]

  // isPinMessage already defined above

  const handleReactionSelect = (emoji) => {
    onAddReaction(message, emoji)
  }

  const handlePinMessageClick = () => {
    if (isPinMessage && message.pinData) {
      if (window.zoomToPin) {
        window.zoomToPin(message.pinData.latitude, message.pinData.longitude)
      }
    }
  }

  if (!hasContent) return null // Don't render empty messages

  // Show upload indicator for temporary messages
  if (message.temp) {
    return (
      <div
        className={`flex flex-col ${isOwnMessage ? 'items-end' : 'items-start'
          } mb-1`}>
        <div className='group relative max-w-[75%] mt-2'>
          {/* Reactions above message */}
          {groupedReactions?.length > 0 && (
            <div
              className={`pin-reaction flex flex-wrap mb-1 space-x-1 absolute -top-3  ${isOwnMessage ? 'justify-end right-1' : 'justify-start left-1'
                }`}>
              {groupedReactions.map((reaction, idx) => {
                const hasReacted = reaction.users?.includes(
                  auth.currentUser?.uid
                )
                return (
                  <button
                    key={idx}
                    onClick={(e) => {
                      e.stopPropagation()
                      onAddReaction(message, reaction.emoji)
                    }}
                    className={`cursor-pointer inline-flex items-center rounded-full text-sm transition-all duration-200 p-0 !bg-transparent ${hasReacted
                      ? 'bg-blue-100 text-blue-600'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}>
                    <span className='mr-1'>{reaction.emoji}</span>
                    {reaction.count > 1 && (
                      <span className='text-xs font-medium'>
                        {reaction.count}
                      </span>
                    )}
                  </button>
                )
              })}
            </div>
          )}

          {/* Message content */}
          <div
            className={`px-4 py-3 rounded-2xl shadow-sm ${isOwnMessage
              ? 'bg-blue-500 text-white rounded-br-md'
              : 'bg-white text-gray-900 border border-gray-200 rounded-bl-md'
              }`}>
            {/* Reply preview */}
            {message.replyId && (
              <div className='mb-3 px-2 py-2 bg-blue-50 rounded-lg flex justify-between items-start border-l-4 border-gray-500'>
                <div className='text-sm'>
                  <div className='font-medium mb-1 text-blue-600'>
                    Replying to:
                  </div>
                  <div className='text-gray-600 truncate'>
                    {message.replyPreview || 'a message'}
                  </div>
                </div>
              </div>
            )}

            {!isOwnMessage && (
              <div
                className='flex items-center space-x-2 cursor-pointer'
                onClick={() =>
                  onViewProfile({
                    userId: message.senderId,
                    profilePic: message.senderProfilePic,
                    displayName: { stringValue: message.senderName }
                  })
                }>
                <div className='relative w-6 h-6 rounded-full overflow-hidden'>
                  <img
                    src={message.senderProfilePic || getUserAvatar({ displayName: message.senderName })}
                    alt={displayName}
                    className='w-full h-full object-cover'
                  />
                </div>
                <span className='text-xs font-medium text-gray-500'>
                  {displayName}
                </span>
              </div>
            )}

            {/* Message text content */}
            {messageText.trim() !== '' && (
              <div className='text-sm leading-relaxed'>{messageText}</div>
            )}

            {/* Uploading attachments */}
            {message.files?.attachments?.length > 0 && (
              <div className='mt-2 space-y-2'>
                {message.files.attachments.map((attachment, idx) => (
                  <div
                    key={idx}
                    className='rounded-xl overflow-hidden relative'>
                    {/* Upload progress overlay */}
                    <div className='absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center rounded-xl z-10'>
                      <div className='bg-white rounded-full p-2'>
                        <div className='w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin'></div>
                      </div>
                    </div>

                    {/* File preview based on type */}
                    {[
                      'mp4',
                      'mov',
                      'avi',
                      'wmv',
                      'flv',
                      'mkv',
                      'webm',
                      'mpeg',
                      'mpg',
                      '3gp',
                      'm4v'
                    ].includes(attachment.fileType) ? (
                      <div className='relative opacity-70'>
                        <div className='w-32 h-32 bg-gray-200 rounded-lg flex items-center justify-center'>
                          <video className='max-w-full h-auto max-h-32 opacity-50'>
                            <source
                              src={
                                attachment.previewUrl ||
                                URL.createObjectURL(attachment)
                              }
                              type={attachment.type}
                            />
                          </video>
                        </div>
                      </div>
                    ) : (
                      <div className='flex items-center p-3 bg-gray-100 rounded-xl opacity-70'>
                        <Paperclip className='w-4 h-4 text-gray-400 mr-2' />
                        <div>
                          <span className='text-sm block'>
                            {attachment.name}
                          </span>
                          <span className='text-xs text-gray-500'>
                            {(attachment.size / 1024).toFixed(1)} KB
                          </span>
                        </div>
                      </div>
                    )}

                    {/* Progress indicator */}
                    <div className='absolute bottom-2 left-2 right-2 bg-white bg-opacity-80 rounded-full h-2 z-20'>
                      <div
                        className='bg-blue-500 h-2 rounded-full transition-all duration-300'
                        style={{
                          width: `${attachment.uploadProgress || 0}%`
                        }}></div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Message actions (hover only) */}
          <div
            className={`absolute ${isOwnMessage ? 'right-[101%]' : 'left-[100%]'
              }  top-1/2 transform -translate-y-1/2 flex space-x-1 opacity-0 group-hover:opacity-100 transition-opacity`}>
            <button
              onClick={() => onReply(message)}
              className='w-5 h-5 !bg-transparent px-2 cursor-pointer'>
              <Reply className='w-4 h-4 text-gray-500' />
            </button>
            <button
              onClick={(e) => onContextMenu(e, message)}
              className='w-5 h-5 !bg-transparent p-1 cursor-pointer'>
              <MoreVertical className='w-4 h-4 text-gray-500' />
            </button>
          </div>
        </div>

        {/* Timestamp with uploading status */}
        {showTimestamp && (
          <div
            className={`flex items-center space-x-1 mt-1 ${isOwnMessage ? 'justify-end' : 'justify-start'
              }`}>
            <span className='text-xs text-gray-400'>
              Uploading... {Math.round(message.uploadProgress || 0)}%
            </span>
          </div>
        )}
      </div>
    )
  }

  // Regular message (not temporary/uploading)
  return (
    <div
      className={`flex flex-col ${isOwnMessage ? 'items-end' : 'items-start'
        } mb-1`}>
      <div className='group relative max-w-[75%] mt-2'>
        {/* Reactions above message */}
        {groupedReactions?.length > 0 && (
          <div
            className={`pin-reaction flex flex-wrap mb-1 space-x-1 absolute -top-3  ${isOwnMessage ? 'justify-end right-1' : 'justify-start left-1'
              }`}>
            {groupedReactions.map((reaction, idx) => {
              const hasReacted = reaction.users?.includes(auth.currentUser?.uid)
              return (
                <button
                  key={idx}
                  onClick={(e) => {
                    e.stopPropagation()
                    onAddReaction(message, reaction.emoji)
                  }}
                  className={`cursor-pointer inline-flex items-center rounded-full text-sm transition-all duration-200 p-0 !bg-transparent ${hasReacted
                    ? 'bg-blue-100 text-blue-600'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}>
                  <span className='mr-1'>{reaction.emoji}</span>
                  {reaction.count > 1 && (
                    <span className='text-xs font-medium'>
                      {reaction.count}
                    </span>
                  )}
                </button>
              )
            })}
          </div>
        )}
        {/* Message content */}
        <div
          className={`px-4 py-3 rounded-2xl shadow-sm ${isOwnMessage
            ? 'bg-blue-500 text-white rounded-br-md'
            : 'bg-white text-gray-900 border border-gray-200 rounded-bl-md'
            }`}>
          {/* Reply preview */}
          {message.replyId && (
            <div className='mb-3 px-2 py-2 bg-blue-50 rounded-lg flex justify-between items-start border-l-4 border-gray-500'>
              <div className='text-sm'>
                <div className='font-medium mb-1 text-blue-600'>
                  Replying to:
                </div>
                <div className='text-gray-600 truncate'>
                  {message.replyPreview || 'a message'}
                </div>
              </div>
            </div>
          )}
          {!isOwnMessage && (
            <div
              className='flex items-center space-x-2 cursor-pointer'
              onClick={() =>
                onViewProfile({
                  userId: message.senderId,
                  profilePic: message.senderProfilePic,
                  displayName: { stringValue: message.senderName }
                })
              }>
              <div className='relative w-6 h-6 rounded-full overflow-hidden'>
                <img
                  src={message.senderProfilePic || getUserAvatar({ displayName: message.senderName })}
                  alt={displayName}
                  className='w-full h-full object-cover'
                />
              </div>
              <span className='text-xs font-medium text-gray-500'>
                {displayText}
                {/* here will be last name and display name based on the condition */}
              </span>
            </div>
          )}
          {/* Message text content */}
          {messageText.trim() !== '' && (
            <div className='text-sm leading-relaxed'>{messageText}</div>
          )}

          {isPinMessage && (
            <div
              className='flex items-center text-xs text-fff cursor-pointer hover:text- transition-colors'
              onClick={handlePinMessageClick}>
              {(message.message === '📷 Photo added to pin' || message.message === 'Photo added to pin') && <ImageIcon className="w-4 h-4 mr-2 text-blue-500" />}
              {!isCompactPinMessage && <Pin className='w-4 h-4 mr-2 text-blue-500' />}
              <span>{isCompactPinMessage ? message.message : 'Click to view on map'}</span>
            </div>
          )}

          {/* Attachments inside the message bubble */}
          {hasAttachments && !isPinMessage && (
            <div className='space-y-2'>
              {message.files.attachments?.map((attachment, idx) => {
                const isHEIC =
                  attachment.fileType?.toLowerCase() === 'heic' ||
                  attachment.name?.toLowerCase().endsWith('.heic')

                return (
                  <a
                    href={attachment.url}
                    target='_blank'
                    rel='noopener noreferrer'
                    className='flex-1 flex items-center'
                    // For HEIC files, we want to download instead of opening in new tab
                    download={isHEIC}>
                    <div
                      key={idx}
                      className='flex items-center p-3 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors space-x-4'>
                      {/* Show warning for HEIC files */}
                      {isHEIC ? (
                        <div className='w-16 h-16 flex-shrink-0 flex items-center justify-center bg-yellow-100 rounded'>
                          <Info className='w-8 h-8 text-yellow-600' />
                        </div>
                      ) : !videoFileTypes.includes(
                        attachment.fileType.toLowerCase()
                      ) ? (
                        <div className='w-16 h-16 flex-shrink-0 flex items-center'>
                          <img
                            src={attachment.url}
                            alt='Attachment preview'
                            className='!w-[100%] !h-[100%] object-contain rounded'
                          />
                        </div>
                      ) : null}

                      {/* Right-side content */}
                      {isHEIC ? (
                        <div className='flex-1'>
                          <div className='text-sm font-medium text-yellow-800'>
                            HEIC File
                          </div>
                          <div className='text-xs text-yellow-600'>
                            Download to view this image
                          </div>
                          <div className='flex items-center gap-2 mt-1'>
                            <Paperclip className='w-4 h-4 text-gray-400' />
                            <span className='text-sm block'>
                              {attachment.name}
                            </span>
                          </div>
                          <span className='text-xs text-gray-500 block text-end w-full'>
                            {(parseInt(attachment.fileSize) / 1024).toFixed(1)}{' '}
                            KB
                          </span>
                        </div>
                      ) : videoFileTypes.includes(
                        attachment.fileType.toLowerCase()
                      ) ? (
                        <div className='flex-1'>
                          <video controls className='w-full max-h-40'>
                            <source
                              src={attachment.url}
                              type={`video/${attachment.fileType.toLowerCase()}`}
                            />
                          </video>
                        </div>
                      ) : (
                        <a
                          href={attachment.url}
                          target='_blank'
                          rel='noopener noreferrer'
                          className='flex-1 flex items-center'>
                          <div className='flex items-center flex-col text-end gap-2'>
                            <div className='flex items-center gap-2'>
                              <Paperclip className='w-4 h-4 text-gray-400' />
                              <span className='text-sm block'>
                                {attachment.fileType} file
                              </span>
                            </div>
                            <span className='text-xs text-gray-500 block text-end w-full'>
                              {(parseInt(attachment.fileSize) / 1024).toFixed(
                                1
                              )}{' '}
                              KB
                            </span>
                          </div>
                        </a>
                      )}
                    </div>
                  </a>
                )
              })}
            </div>
          )}
        </div>

        {/* Message actions (hover only) */}
        <div
          className={`absolute ${isOwnMessage ? 'right-[101%]' : 'left-[100%]'
            }  top-1/2 transform -translate-y-1/2 flex space-x-1 opacity-0 group-hover:opacity-100 transition-opacity`}>
          {/* <button
              onClick={(e) => {
                e.stopPropagation()
                setShowReactionPicker(!showReactionPicker)
              }}
              className="w-5 h-5 !bg-transparent p-1"
            >
              <Smile className="w-4 h-4 text-gray-500" />
            </button> */}
          {/* {showReactionPicker && (
              <ReactionPicker onSelect={handleReactionSelect} onClose={() => setShowReactionPicker(false)} />
            )} */}
          <button
            onClick={() => onReply(message)}
            className='w-5 h-5 !bg-transparent px-2 cursor-pointer'>
            <Reply className='w-4 h-4 text-gray-500' />
          </button>
          <button
            onClick={(e) => onContextMenu(e, message)}
            className='w-5 h-5 !bg-transparent p-1 cursor-pointer'>
            <MoreVertical className='w-4 h-4 text-gray-500' />
          </button>
        </div>
      </div>
      {/* Timestamp and status */}
      {showTimestamp && (
        <div
          className={`flex items-center space-x-1 mt-1 ${isOwnMessage ? 'justify-end' : 'justify-start'
            }`}>
          <span className='text-xs text-gray-400'>
            <DateTime value={message.timestamp || message.createdAt} />
          </span>
          {isOwnMessage && <Check className='w-3 h-3 text-blue-500' />}
          {message.isEdited && (
            <span className='text-xs text-gray-400'>(edited)</span>
          )}
        </div>
      )}
    </div>
  )
}

function MessageList({
  onReply,
  onAddReaction,
  onContextMenu,
  onViewProfile,
  channelId,
  messages, // Now received as prop
  setMessages, // Now received as prop
  loadingMore, // Now received as prop
  hasMore, // Now received as prop
  initialLoadComplete, // Now received as prop
  loadMoreMessages, // Now received as prop
  messagesEndRef, // Now received as prop
  listRef, // Now received as prop
  isAtBottom,
  setIsAtBottom
}) {
  const currentUserId = auth.currentUser?.uid

  // Handle scroll events for infinite loading
  const handleScroll = useCallback(() => {
    if (listRef.current && !loadingMore && hasMore) {
      const { scrollTop } = listRef.current
      // Check if we're near the top (within 200px)
      if (scrollTop < 200) {
        loadMoreMessages()
      }
    }
    if (listRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = listRef.current
      const isBottom = scrollTop + clientHeight >= scrollHeight - 10 // Small buffer
      setIsAtBottom(isBottom)
    }
  }, [loadMoreMessages, loadingMore, hasMore, listRef, setIsAtBottom])

  // Filter out deleted messages
  const filteredMessages = Array.isArray(messages)
    ? messages.filter((msg) => !msg.deletedBy?.includes(currentUserId))
    : []

  // Group messages by sender and time
  const groupedMessages = useMemo(() => {
    if (filteredMessages.length === 0) return []

    const groups = []
    let currentGroup = {
      senderId: filteredMessages[0].senderId,
      messages: [],
      timestamp: filteredMessages[0].created_at || filteredMessages[0].createdAt
    }

    for (const message of filteredMessages) {
      const getMessageTime = (msg) => {
        if (msg.created_at?.seconds) {
          return msg.created_at.seconds * 1000 // Firestore Timestamp
        }
        if (msg.createdAt?.seconds) {
          return msg.createdAt.seconds * 1000 // Firestore Timestamp (alternative field)
        }
        if (typeof msg.createdAt === 'string') {
          return new Date(msg.createdAt).getTime() // ISO string
        }
        return msg.tempTimestamp || Date.now() // Fallback
      }

      const messageTime = getMessageTime(message)
      const lastMessageTime =
        currentGroup.messages.length > 0
          ? getMessageTime(
            currentGroup.messages[currentGroup.messages.length - 1]
          )
          : 0

      // Group messages from same sender within 5 minutes
      if (
        message.senderId === currentGroup.senderId &&
        messageTime - lastMessageTime < 5 * 60 * 1000
      ) {
        currentGroup.messages.push(message)
      } else {
        groups.push(currentGroup)
        currentGroup = {
          senderId: message.senderId,
          messages: [message],
          timestamp: message.created_at || message.createdAt
        }
      }
    }

    if (currentGroup.messages.length > 0) {
      groups.push(currentGroup)
    }
    return groups
  }, [filteredMessages])

  return (
    <div
      className='flex-1 overflow-y-auto p-4 space-y-6 bg-gray-50'
      ref={listRef}
      onScroll={handleScroll}>
      {loadingMore && (
        <div className='flex justify-center py-4'>
          <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500'></div>
        </div>
      )}
      {groupedMessages.length === 0 && initialLoadComplete && (
        <div className='text-center text-gray-500 py-12'>
          <div className='w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4'>
            <Send className='w-8 h-8 text-gray-400' />
          </div>
          <p>No messages yet. Start the conversation!</p>
        </div>
      )}
      {groupedMessages.map((group, groupIndex) => (
        <div key={groupIndex} className='flex flex-col'>
          {groupIndex === 0 && (
            <div className='flex items-center justify-center mb-6'>
              <div className='text-xs text-gray-400 bg-white px-3 py-1 rounded-full shadow-sm'>
                <DateTime value={group.timestamp} />
              </div>
            </div>
          )}
          {group.messages.map((msg, msgIndex) => (
            <div key={msg.id || msgIndex}>
              <MessageItem
                message={msg}
                onReply={onReply}
                onAddReaction={onAddReaction}
                onContextMenu={onContextMenu}
                onViewProfile={onViewProfile}
                showTimestamp={msgIndex === group.messages.length - 1}
                channelId={channelId}
              />
            </div>
          ))}
        </div>
      ))}

      {!isAtBottom && (
        <button
          onClick={() => {
            if (messagesEndRef.current) {
              messagesEndRef.current.scrollIntoView({ behavior: 'smooth' })
              setIsAtBottom(true)
            }
          }}
          className='cursor-pointer fixed bottom-20 right-[30px] bottom-[85px] p-3 bg-blue-500 text-white rounded-full shadow-lg hover:bg-blue-600 transition-all duration-200 z-50'
          title='Scroll to bottom'>
          <svg
            className='w-6 h-6'
            fill='none'
            stroke='currentColor'
            viewBox='0 0 24 24'
            xmlns='http://www.w3.org/2000/svg'>
            <path
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeWidth='2'
              d='M19 14l-7 7m0 0l-7-7m7 7V3'
            />
          </svg>
        </button>
      )}
      <div ref={messagesEndRef} />
    </div>
  )
}

function MessageInput({
  channelId,
  profile,
  channelData,
  replyingTo,
  onCancelReply,
  uploadProgress,
  setUploadProgress,
  setMessages,
  setShouldAutoScroll
}) {
  const [messageText, setMessageText] = useState('')
  const [isSending, setIsSending] = useState(false)
  const [showEmojiPicker, setShowEmojiPicker] = useState(false)
  const [attachments, setAttachments] = useState([])
  const fileInputRef = useRef(null)
  const [isDragging, setIsDragging] = useState(false)
  const dropRef = useRef(null)
  const [isTyping, setIsTyping] = useState(false)
  const typingTimeoutRef = useRef(null)
  const inputRef = useRef(null)
  const textareaRef = useRef(null)
  const { processFile } = useHeicConverter()

  // Typing indicator effect
  useEffect(() => {
    if (!channelId || !auth.currentUser?.uid) return

    const updateTypingStatus = async (isUserTyping) => {
      try {
        const typingUpdate = {
          [auth.currentUser.uid]: {
            isTyping: isUserTyping,
            name: getDisplayName(profile?.displayName)
            // updateTimestamp: new Date().toISOString(),
          }
        }

        await updateDoc(doc(db, 'chats', channelId), {
          typingStatus: typingUpdate,
        })
      } catch (error) {
        console.error('Error updating typing status:', error)
      }
    }

    if (isTyping) {
      updateTypingStatus(true)

      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current)
      }

      typingTimeoutRef.current = setTimeout(() => {
        setIsTyping(false)
        updateTypingStatus(false)
      }, 3000)
    } else {
      updateTypingStatus(false)
    }

    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current)
      }
    }
  }, [isTyping, channelId, profile])

  // Handle key down in textarea
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault() // Prevent default behavior (new line)
      sendMessage(e) // Submit the form
    }
    // Shift+Enter will work as default (new line)
  }

  // Handle input changes
  const handleInputChange = (e) => {
    const value = e.target.value
    setMessageText(value)

    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto'
      textareaRef.current.style.height = `${Math.min(
        textareaRef.current.scrollHeight,
        150
      )}px` // Max height 150px
    }

    // Update typing status
    if (value.trim().length > 0) {
      if (!isTyping) {
        setIsTyping(true)
      } else {
        // Reset the typing timeout on each keystroke
        if (typingTimeoutRef.current) {
          clearTimeout(typingTimeoutRef.current)
        }
        typingTimeoutRef.current = setTimeout(() => {
          setIsTyping(false)
        }, 3000)
      }
    } else if (isTyping) {
      setIsTyping(false)
    }
  }

  // Handle form submission
  const sendMessage = async (e) => {
    e.preventDefault()
    const text = messageText.trim()

    if (
      (!text && attachments.length === 0) ||
      !channelId ||
      isSending ||
      !auth.currentUser
    )
      return

    // Clear typing status
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current)
    }
    setIsTyping(false)
    setIsSending(true)

    const user = auth.currentUser
    const displayName = profile?.displayName || 'User'
    const currentTime = new Date().toISOString()

    // Create temporary message for immediate UI feedback
    const tempMessageId = `temp-${Date.now()}`
    const tempMessage = {
      id: tempMessageId,
      message: text,
      senderId: user.uid,
      senderName: displayName?.stringValue,
      senderProfilePic: profile?.profileUrl?.stringValue,
      timestamp: currentTime,
      isUploading: true,
      uploadProgress: 0,
      files: {
        attachments: attachments.map((file) => ({
          name: file.name,
          size: file.size,
          type: file.type,
          isUploading: true,
          uploadProgress: 0
        }))
      },
      temp: true,
      tempId: tempMessageId
    }

    // Add temporary message to UI immediately
    setMessages((prev) => [...prev, tempMessage])
    setMessageText('')
    setShouldAutoScroll(true)
    // Reset textarea height
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto'
    }

    // File upload logic
    let fileAttachments = []
    let chatName = 'unknown-chat'
    try {
      const chatRef = doc(db, 'chats', channelId)
      const chatSnap = await getDoc(chatRef)
      if (chatSnap.exists()) {
        const data = chatSnap.data()
        chatName = data?.name || channelId
      }
    } catch (err) {
      console.error('Failed to fetch chat name:', err)
    }

    if (attachments.length > 0) {
      try {
        fileAttachments = await Promise.all(
          attachments.map(async (file, index) => {
            try {
              const filePath = `stage/chats/${chatName}/${file.name
                }_${Date.now()}`
              const storageRef = ref(storage, filePath)

              // Create upload task
              const uploadTask = uploadBytesResumable(storageRef, file)

              // Track upload progress
              return new Promise((resolve, reject) => {
                uploadTask.on(
                  'state_changed',
                  (snapshot) => {
                    const progress =
                      (snapshot.bytesTransferred / snapshot.totalBytes) * 100
                    // Update progress for this specific file
                    setMessages((prev) =>
                      prev.map((msg) =>
                        msg.id === tempMessageId
                          ? {
                            ...msg,
                            uploadProgress: progress,
                            files: {
                              ...msg.files,
                              attachments: msg.files.attachments.map(
                                (att, i) =>
                                  i === index
                                    ? { ...att, uploadProgress: progress }
                                    : att
                              )
                            }
                          }
                          : msg
                      )
                    )
                  },
                  (error) => {
                    console.error(`Error uploading file ${file.name}:`, error)
                    reject(error)
                  },
                  async () => {
                    // Upload completed
                    const downloadURL = await getDownloadURL(
                      uploadTask.snapshot.ref
                    )
                    const fileType = file.type.split('/')[0]
                    const fileExtension = file.name
                      .split('.')
                      .pop()
                      .toUpperCase()

                    const attachment = {
                      fileSize: file.size.toString(),
                      fileType: fileExtension,
                      url: downloadURL,
                      thumbnail:
                        fileType === 'video'
                          ? `https://img.youtube.com/vi/${file.name}/0.jpg`
                          : null
                    }

                    resolve(attachment)
                  }
                )
              })
            } catch (error) {
              console.error(`Error uploading file ${file.name}:`, error)
              return null
            }
          })
        )

        fileAttachments = fileAttachments.filter(Boolean)
      } catch (error) {
        console.error('Error processing file uploads:', error)

        // Remove temporary message on error
        setMessages((prev) => prev.filter((msg) => msg.id !== tempMessageId))
        setIsSending(false)
        return
      }
    }

    try {
      const chatRef = doc(db, 'chats', channelId)
      const chatSnap = await getDoc(chatRef)

      if (chatSnap.exists()) {
        const chatData = chatSnap.data()
        const isMember = chatData.members?.some(
          (member) => member.userId === user.uid
        )

        if (!isMember) {
          await updateDoc(chatRef, {
            members: arrayUnion({
              userId: user.uid,
              displayName,
              profilePic: getUserAvatar(profile),
              addedBy: null,
              isOnline: true
            }),
            // T31: memberIds is the field every membership query reads, and the
            // field the Phase 5 chat rule keys on. Writing members without it
            // left the two out of step.
            memberIds: arrayUnion(user.uid),
            lastUpdatedAt: new Date().toISOString()
          })
        }
      } else {
        throw new Error('Chat channel does not exist')
      }

      const messagesRef = collection(db, 'chats', channelId, 'messages')
      const newMessageRef = doc(messagesRef)
      const messageId = newMessageRef.id

      const message = {
        deleteBy: [],
        files: {
          attachments: fileAttachments,
          caption: text || ''
        },
        message: text,
        messageId: messageId,
        messageLocationData: null,
        isEdited: null,
        messageType: attachments.length > 0 ? 'doc' : 'text',
        reactions: [],
        replyId: replyingTo?.id || null,
        replyPreview: replyingTo
          ? replyingTo.message.substring(0, 50) +
          (replyingTo.message.length > 50 ? '...' : '')
          : null,
        seenBy: [],
        senderId: user.uid,
        senderName: displayName?.stringValue,
        senderProfilePic: profile?.profileUrl?.stringValue,
        starredBy: [],
        timestamp: currentTime,
        tempId: tempMessageId
      }

      await setDoc(newMessageRef, message)

      await updateDoc(chatRef, {
        lastMessage: text || 'Sent an attachment',
        lastMessageId: messageId,
        lastMessageSenderId: user.uid,
        lastUpdatedAt: new Date().toISOString(),
        updateTimestamp: new Date().toISOString(),
        [`typingStatus.${user.uid}`]: deleteField()
      })

      // Remove temporary message and let real-time listener add the actual message
      setMessages((prev) => prev.filter((msg) => msg.id !== tempMessageId))

      if (text.trim()) {
        try {
          await sendNewMessageNotification(
            channelId,
            { id: messageId, message: text },
            displayName,
            channelData
          )
        } catch (notificationError) {
          console.error(
            'Failed to send message notification:',
            notificationError
          )
        }
      }

      if (replyingTo) onCancelReply()
      setAttachments([])
      setMessageText('') // Clear input after sending
    } catch (error) {
      console.error('Error sending message:', error)
      // Remove temporary message on error
      setMessages((prev) => prev.filter((msg) => msg.id !== tempMessageId))
    } finally {
      setIsSending(false)
    }
  }

  // Rest of the component (drag handlers, file handling, emoji picker, etc.)
  const handleDragEnter = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
  }

  const handleDragLeave = (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.currentTarget.contains(e.relatedTarget)) return
    setIsDragging(false)
  }

  const handleDragOver = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
  }

  const handleDrop = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)

    const files = Array.from(e.dataTransfer.files)
    if (files.length > 0) {
      setAttachments((prev) => [...prev, ...files])
    }
  }

  useEffect(() => {
    const dropArea = dropRef.current
    if (dropArea) {
      dropArea.addEventListener('dragenter', handleDragEnter)
      dropArea.addEventListener('dragleave', handleDragLeave)
      dropArea.addEventListener('dragover', handleDragOver)
      dropArea.addEventListener('drop', handleDrop)

      return () => {
        dropArea.removeEventListener('dragenter', handleDragEnter)
        dropArea.removeEventListener('dragleave', handleDragLeave)
        dropArea.removeEventListener('dragover', handleDragOver)
        dropArea.removeEventListener('drop', handleDrop)
      }
    }
  }, [])

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files)

    if (files.length === 0) return

    try {
      setIsSending(true) // Show loading state while processing files

      // Process all files (convert HEIC to JPG if needed)
      const processedFiles = await Promise.all(
        files.map(async (file) => {
          return await processFile(file)
        })
      )

      if (processedFiles.length > 0) {
        setAttachments((prev) => [...prev, ...processedFiles])
      }
    } catch (error) {
      console.error('Error processing files:', error)
      toast.error('Failed to process some files')
    } finally {
      setIsSending(false)
    }
  }

  const removeAttachment = (index) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index))
  }

  const triggerFileInput = () => {
    fileInputRef.current.click()
  }

  const handleEmojiSelect = (emoji) => {
    setMessageText((prev) => prev + emoji)
    setShowEmojiPicker(false)
    if (!isTyping) {
      setIsTyping(true)
    }
  }

  return (
    <div
      className={`p-4 border-t border-gray-200 bg-white relative ${isDragging ? 'bg-blue-50 border-blue-200' : ''
        }`}
      ref={dropRef}>
      {isDragging && (
        <div className='absolute inset-0 bg-blue-50 border-2 border-dashed border-blue-300 rounded-lg flex items-center justify-center z-10'>
          <div className='text-center p-6 bg-white rounded-lg shadow-sm'>
            <Paperclip className='w-10 h-10 text-blue-500 mx-auto mb-3' />
            <p className='text-blue-600 font-medium'>
              Drop files here to upload
            </p>
          </div>
        </div>
      )}

      {replyingTo && (
        <div className='mb-3 px-4 py-3 bg-blue-50 rounded-lg flex justify-between items-start border-l-4 border-blue-500'>
          <div className='text-sm text-blue-600'>
            <div className='font-medium mb-1'>
              Replying to {replyingTo?.senderName}
            </div>
            <div className='text-gray-600 truncate'>{replyingTo.message}</div>
          </div>
          <button
            onClick={onCancelReply}
            className='text-gray-400 hover:text-gray-600 ml-2 cursor-pointer'>
            <X className='w-4 h-4' />
          </button>
        </div>
      )}

      {!isSending && attachments.length > 0 && (
        <div className='mb-3 space-y-2'>
          {attachments.map((file, index) => (
            <div
              key={index}
              className='flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200'>
              <div className='flex items-center space-x-3'>
                <Paperclip className='w-4 h-4 text-gray-400' />
                <div>
                  <span className='text-sm font-medium truncate max-w-xs block'>
                    {file.name}
                  </span>
                  <span className='text-xs text-gray-400'>
                    {(file.size / 1024).toFixed(1)} KB
                  </span>
                </div>
              </div>
              <button
                onClick={() => removeAttachment(index)}
                className='text-gray-400 hover:text-gray-600 p-1 cursor-pointer'>
                <X className='w-4 h-4' />
              </button>
            </div>
          ))}
          <button
            type='button'
            onClick={triggerFileInput}
            className='cursor-pointer w-full p-3 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center text-gray-400 hover:text-gray-600 hover:border-gray-400 transition-colors'>
            <Plus className='w-5 h-5 mr-2' />
            <span>Add more files</span>
          </button>
        </div>
      )}

      <form onSubmit={sendMessage} className='flex items-end space-x-3'>
        <input
          type='file'
          ref={fileInputRef}
          onChange={handleFileChange}
          className='hidden'
          multiple
          accept='image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.heic,.HEIC'
        />
        {attachments.length === 0 && (
          <button
            type='button'
            onClick={triggerFileInput}
            disabled={isSending}
            className={`p-3 ${isSending ? 'text-gray-300 cursor-not-allowed' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'} rounded-full transition-all duration-200 mr-3 cursor-pointer`}
            title={isSending ? 'Processing files...' : 'Attach files'}>
            <Paperclip className='w-5 h-5' />
            {isSending && (
              <div className='absolute inset-0 flex items-center justify-center'>
                <div className='w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin'></div>
              </div>
            )}
          </button>
        )}
        <div className='flex-1 relative'>
          <textarea
            ref={textareaRef}
            placeholder={
              attachments.length > 0
                ? 'Add a message (optional)'
                : 'Type your message...'
            }
            name='message'
            value={messageText}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            disabled={isSending}
            rows={1}
            className='w-full px-4 py-3 pr-12 border border-gray-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 resize-none overflow-hidden min-h-[44px] max-h-[150px]'
            style={{ minHeight: '44px' }}
          />
          <button
            type='button'
            onClick={() => setShowEmojiPicker(!showEmojiPicker)}
            className='absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors cursor-pointer'>
            <Smile className='w-5 h-5' />
          </button>
          {showEmojiPicker && (
            <div className='absolute bottom-full right-0 mb-2 z-10'>
              {/* You would implement your emoji picker component here */}
              {/* Example: <EmojiPicker onSelect={handleEmojiSelect} /> */}
            </div>
          )}
        </div>
        <button
          type='submit'
          disabled={
            isSending || (!messageText.trim() && attachments.length === 0)
          }
          className='p-3 bg-blue-500 text-white rounded-full hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-sm cursor-pointer'>
          <Send className='w-5 h-5' />
        </button>
      </form>
    </div>
  )
}
function GroupDetailsModal({
  channelId,
  channelData,
  onClose,
  profile,
  onLeaveGroup,
  onRemoveMember,
  onTransferOwnership,
  onDeleteGroup,
  onViewProfile,
  onAddMembers,
  onUpdateGroupName
}) {
  const [selectedMember, setSelectedMember] = useState(null)
  const [newOwner, setNewOwner] = useState(null)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [isIncidentActive, setIsIncidentActive] = useState(false)
  const [isEditingName, setIsEditingName] = useState(false)
  const [newGroupName, setNewGroupName] = useState(channelData?.groupName || '')
  const [showAddMembers, setShowAddMembers] = useState(false)

  const currentUserId = auth.currentUser?.uid
  const isAdmin = channelData?.createdBy?.includes(auth.currentUser?.uid)
  const isSubadmin = channelData?.subAdmins?.includes(currentUserId)

  useEffect(() => {
    const checkIncidentStatus = async () => {
      if (channelData?.chatType === 'incident' && channelData?.incidentId) {
        const incidentRef = doc(db, 'incidents', channelData.incidentId)
        const incidentSnap = await getDoc(incidentRef)
        if (incidentSnap.exists()) {
          setIsIncidentActive(incidentSnap.data().status !== 'ended')
        }
      }
    }
    checkIncidentStatus()
  }, [channelData])

  const handleLeaveGroup = async () => {
    if (isIncidentActive) {
      toast.error(
        'You cannot leave an incident chat until the incident is resolved.'
      )
      return
    }

    if (isAdmin && channelData.members.length > 1 && !newOwner) {
      toast.error('Select new admin first')
      return
    }

    try {
      await runTransaction(db, async (transaction) => {
        const chatRef = doc(db, 'chats', channelId)
        const chatDoc = await transaction.get(chatRef)
        if (!chatDoc.exists()) throw new Error('Chat not found')

        const chatData = chatDoc.data()

        // T31: members holds objects, so arrayRemove(currentUserId) with a plain
        // string matched nothing and silently left the admin in the group. The
        // object has to be removed by value, the way handleRemoveMember does it.
        const currentAdminMember = chatData.members?.find(
          (m) => m.userId === currentUserId
        )

        const updates = {
          createdBy: newOwner, // newOwner is userId string
          memberIds: arrayRemove(currentUserId),
          ...(currentAdminMember && {
            members: arrayRemove(currentAdminMember)
          })
        }

        // ✅ If the promoted admin was a subadmin, remove them from subAdmins
        if (chatData.subAdmins?.includes(newOwner)) {
          updates.subAdmins = arrayRemove(newOwner)
        }

        transaction.update(chatRef, updates)
      })

      toast.success('Admin transferred successfully')
      onLeaveGroup?.()
    } catch (error) {
      console.error('Firebase error:', error)
      toast.error(`Transfer failed: ${error.message}`)
    }
  }

  const handleDeleteGroup = async () => {
    if (channelData?.chatType === 'incident' && isIncidentActive) {
      toast.error(
        'Cannot delete an incident chat while the incident is active.'
      )
      return
    }
    onDeleteGroup()
  }

  const handleNameUpdate = async () => {
    if (!newGroupName.trim()) {
      toast.error('Group name cannot be empty')
      return
    }

    if (newGroupName === channelData.groupName) {
      setIsEditingName(false)
      return
    }

    try {
      await onUpdateGroupName(channelId, newGroupName)
      setIsEditingName(false)
      toast.success('Group name updated successfully')
    } catch (error) {
      console.error('Error updating group name:', error)
      toast.error('Failed to update group name')
    }
  }

  return (
    <div className='fixed inset-0 bg-[#000000b8] bg-opacity-50 flex items-center justify-center z-50 p-4'>
      <div className='bg-white rounded-xl w-full max-w-md max-h-[90vh] overflow-y-auto'>
        <div className='py-4 px-4'>
          <div className='flex justify-between items-center mb-3 py-2 px-2'>
            <h2 className='text-xl font-bold text-gray-900'>Group Info</h2>
            <button
              onClick={onClose}
              className='text-gray-400 hover:text-gray-600 cursor-pointer'>
              <X className='w-5 h-5' />
            </button>
          </div>

          {/* Group Name Section */}
          <div className='mb-3'>
            <div className='flex items-center space-x-3'>
              <div className='w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center shadow-sm'>
                <span className='text-white font-semibold text-base'>
                  {channelData?.groupName?.charAt(0).toUpperCase() || 'G'}
                </span>
              </div>
              <div className='flex-1'>
                {isEditingName ? (
                  <div className='flex items-center gap-2'>
                    <input
                      type='text'
                      value={newGroupName}
                      onChange={(e) => setNewGroupName(e.target.value)}
                      className='flex-1 px-2 py-1 border border-gray-300 rounded-md'
                      autoFocus
                    />
                    <button
                      onClick={handleNameUpdate}
                      className='p-1 text-green-600 hover:text-green-800 cursor-pointer'>
                      <Check className='w-5 h-5' />
                    </button>
                    <button
                      onClick={() => {
                        setIsEditingName(false)
                        setNewGroupName(channelData.groupName)
                      }}
                      className='p-1 text-red-600 hover:text-red-800 cursor-pointer'>
                      <X className='w-5 h-5' />
                    </button>
                  </div>
                ) : (
                  <div className='flex items-center justify-between'>
                    <h3 className='font-semibold text-gray-900'>
                      {channelData?.groupName || 'Group Chat'}
                    </h3>
                    {(isAdmin || isSubadmin) &&
                      channelData?.chatType === 'group' && (
                        <button
                          onClick={() => setIsEditingName(true)}
                          className='p-1 text-blue-600 hover:text-blue-800 cursor-pointer'>
                          <Edit className='w-4 h-4' />
                        </button>
                      )}
                  </div>
                )}
                <p className='text-sm text-gray-500'>
                  {channelData?.members?.length || 0} members
                </p>
              </div>
            </div>
          </div>

          {/* Members Section */}
          <div className='mb-4'>
            <div className='flex justify-between items-center mb-3'>
              <h3 className='font-medium text-gray-900'>Members</h3>
              {(isAdmin || isSubadmin) && channelData?.chatType === 'group' && (
                <button
                  onClick={() => setShowAddMembers(true)}
                  className='text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1 cursor-pointer'>
                  <Plus className='w-4 h-4' /> Add members
                </button>
              )}
            </div>
            <div className='space-y-2 max-h-60 overflow-y-auto'>
              {channelData?.members?.map((member) => (
                <div
                  key={member.userId}
                  className='flex items-center justify-between p-3 bg-gray-50 rounded-lg w-full'>
                  <div className='flex items-center space-x-3 w-full'>
                    <div
                      className='relative w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden cursor-pointer'
                      onClick={() => {
                        onViewProfile(member)
                        onClose()
                      }}>
                      <img
                        src={getUserAvatar(member)}
                        alt={member.displayName}
                        className='w-full h-full object-cover'
                      />
                      {/* <div className='text-xs text-gray-500'>
                        {member.isOnline ? 'Online' : 'Offline'}
                      </div> */}
                    </div>
                    <div className='flex flex-col gap-1 w-[calc(100%-50px)]'>
                      <div className='font-medium text-sm text-gray-900 flex items-start gap-2 w-full justify-between'>
                        {getDisplayName(member.displayName)}
                        {channelData?.createdBy?.includes(member.userId) && (
                          <span className='text-xs bg-blue-100 text-blue-800 p-2 py-0.5 rounded-full flex-none'>
                            Admin
                          </span>
                        )}

                        {channelData?.subAdmins?.includes(member.userId) && (
                          <div className='text-xs bg-blue-100 text-blue-800 p-2 py-0.5 rounded-full flex-none'>
                            SubAdmin
                          </div>
                        )}
                        {((isAdmin &&
                          member.userId !== currentUserId &&
                          !member.isAdmin &&
                          !channelData?.createdBy?.includes(member.userId)) ||
                          (isSubadmin &&
                            member.userId !== currentUserId &&
                            !member.isAdmin &&
                            !channelData?.createdBy?.includes(member.userId) &&
                            !channelData?.subAdmins?.includes(
                              member.userId
                            ))) && (
                            <div className='flex items-center gap-2'>
                              <button
                                onClick={() => setSelectedMember(member)}
                                className='text-xs text-red-500 hover:text-red-700 p-2 py-0.5 rounded-full hover:bg-red-50 flex-none cursor-pointer'>
                                Remove
                              </button>
                            </div>
                          )}
                      </div>
                      <div className='text-xs text-gray-500'>
                        {member.userId === currentUserId ? 'You' : ''}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Transfer Ownership Section */}
          {isAdmin && channelData.members.length > 1 && (
            <div className='mb-3'>
              <h3 className='font-medium text-gray-900 mb-3'>
                Transfer Ownership
              </h3>
              <select
                value={newOwner || ''}
                onChange={(e) => setNewOwner(e.target.value)}
                className='w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus-visible:outline-none'>
                <option value=''>Select new admin</option>
                {channelData.members
                  .filter((m) => m.userId !== currentUserId)
                  .map((member) => (
                    <option key={member.userId} value={member.userId}>
                      {member.displayName}
                    </option>
                  ))}
              </select>
            </div>
          )}

          {/* Action Buttons */}
          <div className='pt-3 flex flex-col gap-3'>
            {isAdmin && (
              <button
                onClick={() => setShowDeleteConfirm(true)}
                className='w-full py-2 px-4 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors cursor-pointer'>
                Delete Group
              </button>
            )}
            <button
              onClick={handleLeaveGroup}
              className='w-full py-2 px-4 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors cursor-pointer'>
              {isAdmin && channelData.members.length > 1
                ? 'Transfer & Leave'
                : 'Leave Group'}
            </button>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4'>
          <div className='bg-white rounded-xl p-6 w-full max-w-sm'>
            <h3 className='font-medium text-gray-900 mb-4'>
              Delete this{' '}
              {channelData?.chatType === 'incident' ? 'incident chat' : 'group'}
              ?
            </h3>
            <p className='text-sm text-gray-500 mb-6'>
              {channelData?.chatType === 'incident'
                ? 'This will permanently delete the incident chat and all its messages.'
                : 'This will permanently delete the group and all its messages.'}
            </p>
            <div className='flex justify-end space-x-3'>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className='px-4 py-2 text-gray-600 hover:text-gray-800 cursor-pointer'>
                Cancel
              </button>
              <button
                onClick={handleDeleteGroup}
                className='px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 cursor-pointer'>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Remove Member Confirmation Modal */}
      {selectedMember && (
        <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4'>
          <div className='bg-white rounded-xl p-6 w-full max-w-sm'>
            <h3 className='font-medium text-gray-900 mb-4'>
              Remove {selectedMember.displayName} from group?
            </h3>
            <p className='text-sm text-gray-500 mb-6'>
              This person will no longer have access to this group chat.
            </p>
            <div className='flex justify-end space-x-3'>
              <button
                onClick={() => setSelectedMember(null)}
                className='px-4 py-2 text-gray-600 hover:text-gray-800 cursor-pointer'>
                Cancel
              </button>
              <button
                onClick={() => {
                  onRemoveMember(selectedMember.userId)
                  setSelectedMember(null)
                }}
                className='px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 cursor-pointer'>
                Remove
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Members Modal */}
      {showAddMembers && (
        <AddMembersModal
          channelId={channelId}
          currentMembers={channelData.memberIds}
          onClose={() => setShowAddMembers(false)}
          onAddMembers={onAddMembers}
          profile={profile}
        />
      )}
    </div>
  )
}

// Add Members Modal Component
function AddMembersModal({ channelId, currentMembers, onClose, onAddMembers, profile }) {
  const [selectedUsers, setSelectedUsers] = useState([])
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        // Scoped to the caller's own organisation, not the whole collection.
        // The users rule is `userId == uid() || boundTo(orgIdOf(resource))`,
        // and Firestore proves a list from its CONSTRAINTS rather than from
        // the documents returned — so an unfiltered getDocs is refused even
        // though every document it would return is readable. This was already
        // filtering client-side to the same set, minus the org boundary.
        const orgId = profile?.organization?.id
        if (orgId === undefined || orgId === null) {
          setUsers([])
          setLoading(false)
          return
        }
        const usersRef = collection(db, 'users')
        const usersSnapshot = await getDocs(
          query(usersRef, where('organization.id', '==', orgId))
        )
        const usersList = usersSnapshot.docs
          .map((doc) => ({ id: doc.id, ...doc.data() }))
          .filter(
            (user) =>
              !currentMembers.includes(user.id) &&
              user.id !== auth.currentUser.uid
          )
        setUsers(usersList)
      } catch (error) {
        console.error('Error fetching users:', error)
        toast.error('Failed to load users')
      } finally {
        setLoading(false)
      }
    }

    fetchUsers()
  }, [currentMembers, profile])

  const toggleUserSelection = (userId) => {
    setSelectedUsers((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    )
  }

  const handleAddMembers = async () => {
    if (selectedUsers.length === 0) {
      toast.error('Please select at least one user')
      return
    }

    try {
      await onAddMembers(channelId, selectedUsers)
      onClose()
      toast.success('Members added successfully')
    } catch (error) {
      console.error('Error adding members:', error)
      toast.error('Failed to add members')
    }
  }

  const filteredUsers = users.filter((user) =>
    user.displayName.toLowerCase().includes(searchTerm.toLowerCase())
  )

  return (
    <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4'>
      <div className='bg-white rounded-xl p-6 w-full max-w-md'>
        <div className='flex justify-between items-center mb-4'>
          <h3 className='font-medium text-gray-900'>Add Members</h3>
          <button
            onClick={onClose}
            className='text-gray-400 hover:text-gray-600 cursor-pointer'>
            <X className='w-5 h-5' />
          </button>
        </div>

        <input
          type='text'
          placeholder='Search users...'
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className='w-full px-4 py-2 border border-gray-300 rounded-lg mb-4 focus:ring-2 focus:ring-blue-500 focus-visible:outline-none'
        />

        <div className='max-h-60 overflow-y-auto mb-4'>
          {loading ? (
            <div className='text-center py-4'>Loading users...</div>
          ) : filteredUsers.length === 0 ? (
            <div className='text-center py-4 text-gray-500'>
              {searchTerm ? 'No users found' : 'No users available to add'}
            </div>
          ) : (
            filteredUsers.map((user) => (
              <div
                key={user.id}
                className={`flex items-center p-3 rounded-lg cursor-pointer ${selectedUsers.includes(user.id)
                  ? 'bg-blue-50'
                  : 'hover:bg-gray-50'
                  }`}
                onClick={() => toggleUserSelection(user.id)}>
                {/* <input
                  type='checkbox'
                  checked={selectedUsers.includes(user.id)}
                  onChange={(e) => {
                    e.stopPropagation()
                    toggleUserSelection(user.id)
                  }}
                  className='mr-3'
                /> */}
                <img
                  src={getUserAvatar(user)}
                  alt={user.displayName}
                  className='w-10 h-10 rounded-full mr-3'
                />
                <span className='font-medium'>{user.displayName}</span>
              </div>
            ))
          )}
        </div>

        <div className='flex justify-end space-x-3'>
          <button
            onClick={onClose}
            className='px-4 py-2 text-gray-600 hover:text-gray-800 cursor-pointer'>
            Cancel
          </button>
          <button
            onClick={handleAddMembers}
            className='px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 cursor-pointer'>
            Add Selected
          </button>
        </div>
      </div>
    </div>
  )
}

export default function ChatPanel({
  customActiveChannel,
  setCustomActiveChannel
}) {
  const [profile] = useApiRequest(
    auth.currentUser?.uid ? `users/${auth.currentUser.uid}` : null
  )
  const [isChatOpen, setIsChatOpen] = useState(false)
  const [showGroupDetails, setShowGroupDetails] = useState(false)
  const [uploadProgress, setUploadProgress] = useState({})
  const [channels, setChannels] = useState([])
  const [channelData, setChannelData] = useState(null)
  const [createChannelModalActive, setCreateChannelModalActive] =
    useState(false)
  const [editChannelModalActive, setEditChannelModalActive] = useState(false)
  const [showAttachments, setShowAttachments] = useState(false)
  const [replyingTo, setReplyingTo] = useState(null)
  const { isIncident, isIncidentActive } = useIncidentStatus(channelData)
  const [contextMenu, setContextMenu] = useState({
    isOpen: false,
    position: { x: 0, y: 0 },
    message: null
  })
  const [viewingUser, setViewingUser] = useState(null)
  const [viewingUserId, setViewingUserId] = useState(null)
  const [showPinnedMessages, setShowPinnedMessages] = useState(false)
  const [isAtBottom, setIsAtBottom] = useState(true)

  // MessageList state management moved to ChatPanel
  const [messages, setMessages] = useState([])
  const [loadingMore, setLoadingMore] = useState(false)
  const [hasMore, setHasMore] = useState(true)
  const messagesEndRef = useRef(null)
  const listRef = useRef(null)
  const [initialLoadComplete, setInitialLoadComplete] = useState(false)
  const [lastDocument, setLastDocument] = useState(null)
  const [newestMessageTime, setNewestMessageTime] = useState(null)
  const [shouldAutoScroll, setShouldAutoScroll] = useState(true)
  const previousScrollHeight = useRef(0)
  const BATCH_SIZE = 10

  useEffect(() => {
    return () => {
      // Clean up typing status when component unmounts
      if (customActiveChannel && auth.currentUser?.uid) {
        updateDoc(doc(db, 'chats', customActiveChannel), {
          [`typingStatus.${auth.currentUser.uid}`]: deleteField()
        }).catch(console.error)
      }
    }
  }, [customActiveChannel])

  useEffect(() => {
    const handleClickOutside = () => {
      if (contextMenu.isOpen) {
        setContextMenu({ ...contextMenu, isOpen: false })
      }
    }
    document.addEventListener('click', handleClickOutside)
    return () => document.removeEventListener('click', handleClickOutside)
  }, [contextMenu])

  const handleViewProfile = (user) => {
    setViewingUserId(user?.userId || null)
    setShowAttachments(false)
  }

  const onAddMembers = async (channelId, userIds) => {
    if (!channelId || !userIds || userIds.length === 0) return

    const chatRef = doc(db, 'chats', channelId)

    try {
      // Fetch all users whose IDs are in the userIds array
      const usersRef = collection(db, 'users')
      const q = query(usersRef, where('__name__', 'in', userIds)) // "__name__" = document ID
      const querySnapshot = await getDocs(q)

      // Build members from the matched documents
      const newMembers = querySnapshot.docs.map((docSnap) => {
        const userData = docSnap.data()
        return {
          userId: docSnap.id,
          displayName: userData.displayName || '',
          profilePic: userData.profilePic || '',
          isOnline: userData.isOnline ?? false
        }
      })

      if (newMembers.length === 0) {
        console.warn('No valid members found to add')
        return
      }

      await updateDoc(chatRef, {
        members: arrayUnion(...newMembers),
        memberIds: arrayUnion(...newMembers.map((m) => m.userId)),
        lastUpdatedAt: new Date().toISOString(),
        updateTimestamp: new Date().toISOString()
      })

      for (const userId of userIds) {
        try {
          await sendMemberAddedNotification(
            channelId,
            channelData.groupName || channelData.name,
            userId
          )
        } catch (notificationError) {
          console.error(
            `Failed to send notification to user ${userId}:`,
            notificationError
          )
        }
      }
    } catch (error) {
      console.error('Error adding members:', error)
      throw error
    }
  }

  const onUpdateGroupName = async (channelId, newName) => {
    if (!channelId || !newName.trim()) return
    const chatRef = doc(db, 'chats', channelId)
    try {
      await updateDoc(chatRef, {
        groupName: newName.trim(),
        lastUpdatedAt: new Date().toISOString(),
        updateTimestamp: new Date().toISOString()
      })
    } catch (error) {
      console.error('Error updating group name:', error)
      throw error
    }
  }

  useEffect(() => {
    if (!auth.currentUser) return

    const q = query(
      collection(db, 'chats'),
      where('memberIds', 'array-contains', auth.currentUser.uid)
    )

    const unsubscribe = onSnapshot(q, (snapshot) => {
      setChannels(
        snapshot.docs
          .map((doc) => ({ id: doc.id, ...doc.data() }))
          .filter((chat) => chat.isDeleted !== true) // includes undefined + false
      )
    })

    return unsubscribe
  }, [])

  useEffect(() => {
    if (!customActiveChannel) {
      setChannelData(null)
      // Reset message states when channel changes or is deselected
      setMessages([])
      setInitialLoadComplete(false)
      setLastDocument(null)
      setNewestMessageTime(null)
      setHasMore(true)
      setShouldAutoScroll(true)
      return
    }
    // Reset messages when channel changes
    setMessages([])
    setInitialLoadComplete(false)
    setLastDocument(null)
    setNewestMessageTime(null)
    setHasMore(true)
    setShouldAutoScroll(true)

    const unsubscribe = onSnapshot(
      doc(db, 'chats', customActiveChannel),
      (doc) => {
        if (doc.exists()) {
          const data = doc.data()
          setChannelData(data)
        } else {
          setChannelData(null)
        }
      },
      (error) => {
        console.error('Error fetching channel data:', error)
      }
    )
    return () => unsubscribe()
  }, [customActiveChannel])

  // Load initial messages with pagination (moved from MessageList)
  useEffect(() => {
    if (!customActiveChannel) return

    const loadInitialMessages = async () => {
      try {
        const messagesRef = collection(
          db,
          'chats',
          customActiveChannel,
          'messages'
        )
        const q = query(
          messagesRef,
          orderBy('timestamp', 'desc'),
          limit(BATCH_SIZE)
        )

        const querySnapshot = await getDocs(q)
        const messagesData = []

        querySnapshot.forEach((doc) => {
          messagesData.push({ id: doc.id, ...doc.data() })
        })

        // Reverse to maintain chronological order
        const reversedMessages = messagesData.reverse()
        setMessages(reversedMessages)

        // Set the newest message time for real-time listener
        if (reversedMessages.length > 0) {
          const newest = reversedMessages[reversedMessages.length - 1]
          setNewestMessageTime(newest.timestamp)
        } else {
          // If no messages yet, set to current time to catch new messages
          setNewestMessageTime(new Date().toISOString())
        }

        setLastDocument(querySnapshot.docs[querySnapshot.docs.length - 1])
        setHasMore(querySnapshot.docs.length === BATCH_SIZE)
        setInitialLoadComplete(true)
        setShouldAutoScroll(true) // Auto-scroll only for initial load
      } catch (error) {
        console.error('Error loading messages:', error)
        setInitialLoadComplete(true)
      }
    }

    loadInitialMessages()
  }, [customActiveChannel])

  // Real-time listener for new messages only (moved from MessageList)
  // Real-time listener for new messages only
  // Real-time listener for new messages only
  useEffect(() => {
    if (!customActiveChannel || !initialLoadComplete || !newestMessageTime)
      return

    const messagesRef = collection(db, 'chats', customActiveChannel, 'messages')
    const q = query(
      messagesRef,
      where('timestamp', '>', newestMessageTime),
      orderBy('timestamp', 'asc')
    )

    const unsubscribe = onSnapshot(
      q,
      async (snapshot) => {
        const newMessages = []
        snapshot.forEach((doc) => {
          newMessages.push({ id: doc.id, ...doc.data() })
        })

        if (newMessages.length > 0) {
          setMessages((prev) => {
            // 1. Filter out any temporary messages that match the new messages by tempId
            const filteredPrev = prev.filter(
              (msg) =>
                !msg.temp ||
                !newMessages.some((newMsg) => newMsg.tempId === msg.tempId)
            )

            // 2. Create Sets for both message IDs and pin IDs
            const existingMessageIds = new Set(filteredPrev.map(msg => msg.id))
            const existingPinIds = new Set(
              filteredPrev
                .filter(msg => msg.pinData?.pinId)
                .map(msg => msg.pinData.pinId)
            )

            // 3. Filter new messages to only include those that don't already exist
            const trulyNewMessages = newMessages.filter(msg => {
              // Check if message ID already exists
              if (existingMessageIds.has(msg.id)) return false

              // Check if pin message with same pinId already exists
              if (msg.pinData?.pinId && existingPinIds.has(msg.pinData.pinId)) return false

              return true
            })

            // 4. Only add messages that are truly new
            return [...filteredPrev, ...trulyNewMessages]
          })

          // Update newest message time
          if (newMessages.length > 0) {
            const newest = newMessages[newMessages.length - 1]
            setNewestMessageTime(newest.timestamp)

            // Only auto-scroll if we actually added new messages
            setShouldAutoScroll(true)
          }
        }
      },
      (error) => {
        console.error('Error in real-time listener:', error)
      }
    )

    return () => unsubscribe()
  }, [customActiveChannel, initialLoadComplete, newestMessageTime])

  // Real-time listener for message updates (reactions, edits, deletions) (moved from MessageList)
  // Real-time listener for message updates (reactions, edits, deletions)
  useEffect(() => {
    if (!customActiveChannel || !initialLoadComplete) return

    const messagesRef = collection(db, 'chats', customActiveChannel, 'messages')

    const unsubscribe = onSnapshot(
      messagesRef,
      (snapshot) => {
        snapshot.docChanges().forEach((change) => {
          if (change.type === 'modified') {
            const updatedMessage = { id: change.doc.id, ...change.doc.data() }

            // Convert old reaction format to new format if needed
            if (
              updatedMessage.reactions &&
              Array.isArray(updatedMessage.reactions)
            ) {
              // Check if it's in the old format (has count property)
              const hasOldFormat = updatedMessage.reactions.some((r) =>
                r.hasOwnProperty('count')
              )

              if (hasOldFormat) {
                // Convert old format to new format
                const newReactions = []
                updatedMessage.reactions.forEach((oldReaction) => {
                  if (oldReaction.users && Array.isArray(oldReaction.users)) {
                    oldReaction.users.forEach((userId) => {
                      newReactions.push({
                        reaction: oldReaction.emoji,
                        userId: userId,
                        timestamp: new Date().toISOString() // Approximate timestamp
                      })
                    })
                  }
                })

                // Update the message with the new format
                updatedMessage.reactions = newReactions

                // Update Firestore with the new format
                updateDoc(
                  doc(
                    db,
                    'chats',
                    customActiveChannel,
                    'messages',
                    updatedMessage.id
                  ),
                  {
                    reactions: newReactions
                  }
                ).catch(console.error)
              }
            }

            setMessages((prevMessages) => {
              // Check if this is a pin message
              const isPinMessage = updatedMessage.pinData?.pinId

              if (isPinMessage) {
                // For pin messages, check by pinData.pinId instead of message id
                const existingIndex = prevMessages.findIndex(msg =>
                  msg.pinData?.pinId === updatedMessage.pinData.pinId
                )

                if (existingIndex >= 0) {
                  // Update existing pin message
                  const updatedMessages = [...prevMessages]
                  updatedMessages[existingIndex] = {
                    ...updatedMessages[existingIndex],
                    ...updatedMessage,
                    id: updatedMessage.id // Keep the original Firestore document ID
                  }
                  return updatedMessages
                } else {
                  // New pin message
                  return [...prevMessages, updatedMessage]
                }
              } else {
                // For regular messages, check by message id
                const existingIndex = prevMessages.findIndex(msg => msg.id === updatedMessage.id)

                if (existingIndex >= 0) {
                  // Update existing message
                  const updatedMessages = [...prevMessages]
                  updatedMessages[existingIndex] = {
                    ...updatedMessages[existingIndex],
                    ...updatedMessage
                  }
                  return updatedMessages
                } else {
                  // New message
                  return [...prevMessages, updatedMessage]
                }
              }
            })
          } else if (change.type === 'removed') {
            // Handle deleted messages
            const deletedMessageId = change.doc.id

            setMessages((prevMessages) => {
              // Filter out the deleted message
              return prevMessages.filter(msg => msg.id !== deletedMessageId)
            })

          }
        })
      },
      (error) => {
        console.error('Error in message updates listener:', error)
      }
    )

    return () => unsubscribe()
  }, [customActiveChannel, initialLoadComplete])

  // Auto-scroll to bottom only when needed (moved from MessageList)
  useEffect(() => {
    if (messagesEndRef.current && shouldAutoScroll && initialLoadComplete) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' })
      setShouldAutoScroll(false) // Reset after scrolling
    }
  }, [messages, shouldAutoScroll, initialLoadComplete])

  // Load more historical messages (moved from MessageList)
  const loadMoreMessages = useCallback(async () => {
    if (!customActiveChannel || loadingMore || !hasMore || !lastDocument) return

    setLoadingMore(true)

    // Store current scroll height before loading new messages
    if (listRef.current) {
      previousScrollHeight.current = listRef.current.scrollHeight
    }

    try {
      const messagesRef = collection(
        db,
        'chats',
        customActiveChannel,
        'messages'
      )
      const q = query(
        messagesRef,
        orderBy('timestamp', 'desc'),
        startAfter(lastDocument),
        limit(BATCH_SIZE)
      )

      const querySnapshot = await getDocs(q)
      const newMessages = []
      querySnapshot.forEach((doc) => {
        newMessages.push({ id: doc.id, ...doc.data() })
      })

      if (newMessages.length > 0) {
        // Reverse to maintain chronological order and prepend to existing messages
        const reversedNewMessages = newMessages.reverse()
        setMessages((prev) => [...reversedNewMessages, ...prev])
        setLastDocument(querySnapshot.docs[querySnapshot.docs.length - 1])
        setHasMore(querySnapshot.docs.length === BATCH_SIZE)

        // Don't auto-scroll for historical messages
        setShouldAutoScroll(false)
      } else {
        setHasMore(false)
      }
    } catch (error) {
      console.error('Error loading more messages:', error)
      toast.error('Failed to load older messages.')
    } finally {
      setLoadingMore(false)
    }
  }, [customActiveChannel, lastDocument, loadingMore, hasMore, listRef])

  // Maintain scroll position after loading older messages (moved from MessageList)
  useEffect(() => {
    if (
      listRef.current &&
      previousScrollHeight.current > 0 &&
      !shouldAutoScroll
    ) {
      const newScrollHeight = listRef.current.scrollHeight
      const heightDifference = newScrollHeight - previousScrollHeight.current

      if (heightDifference > 0) {
        listRef.current.scrollTop = heightDifference
        previousScrollHeight.current = 0 // Reset
      }
    }
  }, [messages, shouldAutoScroll, listRef])

  const handleLeaveGroup = useCallback(async () => {
    try {
      const userId = auth.currentUser?.uid
      if (!userId || !customActiveChannel) return

      // Get the current channel data
      const channelRef = doc(db, 'chats', customActiveChannel)
      const channelSnap = await getDoc(channelRef)
      if (!channelSnap.exists()) {
        throw new Error('Channel not found')
      }

      const channelData = channelSnap.data()

      // Find the exact member object to remove
      const memberToRemove = channelData.members.find(
        (m) => m.userId === userId
      )
      if (!memberToRemove) {
        throw new Error('User not found in channel members')
      }

      await updateDoc(channelRef, {
        members: arrayRemove(memberToRemove),
        // T31: without this the user stays in memberIds, so the group keeps
        // appearing in their chat list and the Phase 5 rule keeps granting access.
        memberIds: arrayRemove(userId),
        lastUpdatedAt: new Date().toISOString()
      })

      setCustomActiveChannel(null)
      setShowGroupDetails(false)
      toast.success('You have left the group successfully')
    } catch (error) {
      console.error('Error leaving group:', error)
      toast.error(`Failed to leave group: ${error.message}`)
    }
  }, [customActiveChannel, setCustomActiveChannel])

  const handleRemoveMember = useCallback(
    async (memberId) => {
      try {
        if (!customActiveChannel) return

        const channelRef = doc(db, 'chats', customActiveChannel)
        const channelSnap = await getDoc(channelRef)
        if (!channelSnap.exists()) {
          throw new Error('Channel not found')
        }

        const channelData = channelSnap.data()

        if (channelData.chatType === 'incident' && channelData.incidentId) {
          // Get incident status from incidents collection
          const incidentRef = doc(db, 'incidents', channelData.incidentId)
          const incidentSnap = await getDoc(incidentRef)
          if (incidentSnap.exists()) {
            const incidentStatus = incidentSnap.data().status
            if (incidentStatus !== 'ended') {
              throw new Error(
                'Cannot remove members from an inprogress incident'
              )
            }
          }
        }

        // Find the exact member object to remove
        const memberToRemove = channelData.members.find(
          (m) => m.userId === memberId
        )
        if (!memberToRemove) {
          throw new Error('Member not found in channel')
        }

        await updateDoc(channelRef, {
          members: arrayRemove(memberToRemove),
          memberIds: arrayRemove(memberId),
          lastUpdatedAt: new Date().toISOString()
        })

        try {
          const removedMemberNotification = {
            topic: `${process.env.REACT_APP_ENV === 'production' ? 'p' : ''
              }livealarm-chat-common-${memberId}`,
            title: 'Removed from Group',
            body: `You were removed from "${channelData.groupName || 'the group'
              }"`,
            data: {
              type: 'chat_message',
              chatId: customActiveChannel
            }
          }

          await sendChatNotification(removedMemberNotification)
        } catch (notificationError) {
          console.error(
            `Failed to send removal notification to user ${memberId}:`,
            notificationError
          )
        }

        toast.success('Member removed successfully')
      } catch (error) {
        console.error('Error removing member:', error)
        toast.error(`Failed to remove member: ${error.message}`)
      }
    },
    [customActiveChannel]
  )

  const handleTransferOwnership = useCallback(
    async (newOwnerId) => {
      try {
        if (!customActiveChannel || !newOwnerId) return

        const channelRef = doc(db, 'chats', customActiveChannel)
        const userId = auth.currentUser?.uid

        // 1. First get current channel data
        const channelSnap = await getDoc(channelRef)
        if (!channelSnap.exists()) {
          throw new Error('Channel not found')
        }

        const channelData = channelSnap.data()

        if (isIncident && isIncidentActive) {
          throw new Error('Cannot transfer ownership during active incident')
        }

        // 2. Find the exact member object to remove (current user)
        const memberToRemove = channelData.members.find(
          (m) => m.userId === userId
        )
        if (!memberToRemove) {
          throw new Error('Current user not found in channel members')
        }

        // 3. Verify new owner exists in members
        const newOwnerExists = channelData.members.some(
          (m) => m.userId === newOwnerId
        )
        if (!newOwnerExists) {
          throw new Error('New owner is not a group member')
        }

        const updatedCreatedBy = [
          ...channelData.createdBy.filter((id) => id !== userId), // Keep all admins except the leaving user
          newOwnerId // Add the new owner
        ]

        // 4. Perform atomic update - both transfer and leave in one operation
        await updateDoc(channelRef, {
          createdBy: updatedCreatedBy,
          members: arrayRemove(memberToRemove),
          // T31: same as handleLeaveGroup -- memberIds has to drop too.
          memberIds: arrayRemove(userId),
          lastUpdatedAt: new Date().toISOString()
        })

        // 5. Update UI state
        setCustomActiveChannel(null)
        setShowGroupDetails(false)
      } catch (error) {
        console.error('Error in transfer/leave process:', error)
        alert(`Operation failed: ${error.message}`)
      }
    },
    [customActiveChannel, setCustomActiveChannel]
  )

  const handleDeleteGroup = useCallback(async () => {
    if (!customActiveChannel) return

    try {
      // Check if it's an active incident (client-side check)
      const channelRef = doc(db, 'chats', customActiveChannel)
      const channelSnap = await getDoc(channelRef)

      if (!channelSnap.exists()) {
        throw new Error('Channel not found')
      }

      const channelData = channelSnap.data()

      if (channelData.chatType === 'incident' && channelData.incidentId) {
        const incidentRef = doc(db, 'incidents', channelData.incidentId)
        const incidentSnap = await getDoc(incidentRef)
        if (incidentSnap.exists() && incidentSnap.data().status !== 'ended') {
          throw new Error('Cannot delete an active incident chat')
        }
      }

      // Call your EXISTING Cloud Function
      const functions = getFunctions()
      const deleteChat = httpsCallable(functions, `deleteChat${ENV_SUFFIX}`)

      const result = await deleteChat({
        chatId: customActiveChannel
      })

      if (result.data.success) {
        setCustomActiveChannel(null)
        setShowGroupDetails(false)
        toast.success('Group deleted successfully')
      } else {
        throw new Error('Failed to delete group')
      }
    } catch (error) {
      console.error('Error deleting group:', error)
      toast.error(`Failed to delete group: ${error.message}`)
    }
  }, [customActiveChannel, setCustomActiveChannel])

  const handleReply = (message) => {
    setReplyingTo(message)
    setTimeout(() => {
      const input = document.querySelector('input[name="message"]')
      if (input) input.focus()
    }, 100)
  }

  const handleAddReaction = async (message, emoji) => {
    try {
      if (!customActiveChannel || !message?.id) {
        console.error('Missing channel or message ID')
        return
      }

      const messageRef = doc(
        db,
        'chats',
        customActiveChannel,
        'messages',
        message.id
      )
      const userId = auth.currentUser?.uid

      if (!userId) {
        console.error('No authenticated user')
        return
      }

      // Get current reactions first
      const messageDoc = await getDoc(messageRef)
      if (!messageDoc.exists()) return

      const currentReactions = messageDoc.data().reactions || []
      const userReactionIndex = currentReactions.findIndex(
        (r) => r.userId === userId && r.reaction === emoji
      )

      let updatedReactions = [...currentReactions]

      if (userReactionIndex >= 0) {
        // User already reacted with this emoji - remove their reaction
        updatedReactions = currentReactions.filter(
          (_, i) => i !== userReactionIndex
        )
      } else {
        // Add new reaction
        updatedReactions.push({
          reaction: emoji,
          timestamp: new Date().toISOString(),
          userId: userId
        })
      }

      // Update Firestore
      await updateDoc(messageRef, {
        reactions: updatedReactions
      })

      // Also update the lastUpdatedAt timestamp on the channel
      await updateDoc(doc(db, 'chats', customActiveChannel), {
        updateTimestamp: `${new Date().toISOString()}`
      })
    } catch (error) {
      console.error('Error updating reaction:', error)
      toast.error('Failed to update reaction.')
    }
  }

  const handleContextMenu = (e, message) => {
    e.preventDefault()
    setContextMenu({
      isOpen: true,
      position: { x: e.clientX, y: e.clientY },
      message: message
    })
  }

  const handleContextAction = async (action) => {
    const message = contextMenu.message
    const userId = auth.currentUser?.uid
    if (!message || !userId || !customActiveChannel) {
      console.error(
        'Missing message, user ID, or channel ID for context action.'
      )
      setContextMenu({ ...contextMenu, isOpen: false })
      return
    }

    const messageRef = doc(
      db,
      'chats',
      customActiveChannel,
      'messages',
      message.id
    )
    const isSender = message.senderId === userId
    const messageTime = message.createdAt?.seconds
      ? message.createdAt.seconds * 1000
      : message.tempTimestamp || Date.now()
    const currentTime = Date.now()
    const oneMinute = 60 * 1000

    try {
      switch (action) {
        case 'delete':
          if (isSender && currentTime - messageTime <= oneMinute) {
            // Hard delete for sender within 1 minute
            if (
              window.confirm(
                'Are you sure you want to permanently delete this message for everyone?'
              )
            ) {
              // Optimistic UI update: remove message immediately
              setMessages((prev) => prev.filter((msg) => msg.id !== message.id))
              await deleteDoc(messageRef)
              await updateDoc(doc(db, 'chats', customActiveChannel), {
                updateTimestamp: new Date().toISOString()
              })
              toast.success('Message deleted successfully.')
            }
          } else {
            // Soft delete for sender after 1 minute or for other users
            if (
              window.confirm('This will remove the message for you. Continue?')
            ) {
              const originalDeletedBy = message.deletedBy || []
              // Optimistic UI update: add userId to deletedBy
              setMessages((prev) =>
                prev.map((msg) =>
                  msg.id === message.id
                    ? { ...msg, deletedBy: [...(msg.deletedBy || []), userId] }
                    : msg
                )
              )
              await updateDoc(messageRef, {
                deletedBy: arrayUnion(userId)
              })
              toast.success('Message removed for you.')
            }
          }
          break
        case 'edit':
          if (!isSender) {
            toast.error('You can only edit your own messages.')
            break
          }
          const newText = prompt('Edit your message:', message.message)
          if (newText && newText.trim() !== message.message.trim()) {
            const originalMessageText = message.message
            const originalIsEdited = message.isEdited
            // Optimistic UI update
            setMessages((prev) =>
              prev.map((msg) =>
                msg.id === message.id
                  ? {
                    ...msg,
                    message: newText.trim(),
                    isEdited: true,
                    // Also update files caption if it exists
                    files: msg.files
                      ? {
                        ...msg.files,
                        caption: newText.trim()
                      }
                      : msg.files
                  }
                  : msg
              )
            )
            await updateDoc(messageRef, {
              message: newText.trim(),
              isEdited: true,
              editedAt: new Date().toISOString(),
              ...(message.files && {
                'files.caption': newText.trim()
              })
            })
            await updateDoc(doc(db, 'chats', customActiveChannel), {
              updateTimestamp: new Date().toISOString()
            })
            toast.success('Message edited.')
          }
          break
        case 'pin':
          const channelRef = doc(db, 'chats', customActiveChannel)
          const channelSnap = await getDoc(channelRef)

          if (channelSnap.exists()) {
            const currentPinned = channelSnap.data().pinnedMessages || []

            // FIX: Check if message is pinned by looking for messageId in objects
            const isPinned = currentPinned.some(
              (pinnedMsg) => pinnedMsg.messageId === message.id
            )

            if (isPinned) {
              const pinnedMessageToRemove = currentPinned.find(
                (pinnedMsg) => pinnedMsg.messageId === message.id
              )
              if (pinnedMessageToRemove) {
                // Create a new array without the message to remove
                const updatedPinnedMessages = currentPinned.filter(
                  (pinnedMsg) => pinnedMsg.messageId !== message.id
                )

                await updateDoc(channelRef, {
                  pinnedMessages: updatedPinnedMessages, // Replace the entire array
                  lastUpdatedAt: new Date().toISOString()
                })
                toast.success('Message unpinned.')
              } else {
                toast.error('Could not find pinned message to remove.')
              }
            } else {
              const pinnerName = getDisplayName(profile?.displayName)
              const pinnedMessageData = {
                messageId: message.id,
                message: message.message,
                pinnedBy: pinnerName
              }

              // Create a new array with the new pinned message added
              const updatedPinnedMessages = [
                ...currentPinned,
                pinnedMessageData
              ]

              await updateDoc(channelRef, {
                pinnedMessages: updatedPinnedMessages, // Replace the entire array
                lastUpdatedAt: new Date().toISOString()
              })

              toast.success('Message pinned.')

              try {
                const chatData = channelSnap.data()
                await sendMessagePinnedNotification(
                  customActiveChannel,
                  chatData.groupName || chatData.name,
                  pinnerName
                )
              } catch (notificationError) {
                console.error(
                  'Failed to send pin notification:',
                  notificationError
                )
              }
            }
          }
          break
      }
    } catch (error) {
      console.error('Error performing action:', error)
      toast.error(`Failed to perform action: ${error.message}`)
      // Revert UI changes on error for delete/edit if possible
      if (
        action === 'delete' &&
        isSender &&
        currentTime - messageTime <= oneMinute
      ) {
        // If hard delete failed, add message back to UI
        setMessages((prev) =>
          [...prev, message].sort(
            (a, b) => (a.createdAt?.seconds || 0) - (b.createdAt?.seconds || 0)
          )
        )
      } else if (action === 'delete') {
        // If soft delete failed, remove userId from deletedBy
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === message.id
              ? {
                ...msg,
                deletedBy: (msg.deletedBy || []).filter((id) => id !== userId)
              }
              : msg
          )
        )
      } else if (action === 'edit') {
        // If edit failed, revert message text and edited status
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === message.id
              ? {
                ...msg,
                message: message.message,
                isEdited: message.isEdited,
                // Also revert files caption
                files: msg.files
                  ? {
                    ...msg.files,
                    caption: message.message
                  }
                  : msg.files
              }
              : msg
          )
        )
      }
    } finally {
      setContextMenu({ ...contextMenu, isOpen: false })
    }
  }

  if (!auth.currentUser) {
    return (
      <div className='flex items-center justify-center h-full bg-gray-50'>
        <div className='text-center'>
          <div className='w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4'>
            <Send className='w-8 h-8 text-gray-400' />
          </div>
          <p className='text-gray-500'>Please log in to chat.</p>
        </div>
      </div>
    )
  }

  return (
    <div className='flex h-full bg-white shadow-lg rounded-lg overflow-hidden chat-container-module'>
      {/* Sidebar - Always visible */}
      <div className='w-80 border-r border-gray-200 flex flex-col bg-white'>
        <div className='p-4 border-b border-gray-200'>
          <h2 className='text-lg font-semibold text-gray-900'>Chats</h2>
        </div>
        <ChannelListContainer
          channels={channels}
          setActiveChannel={setCustomActiveChannel}
          customActiveChannel={customActiveChannel}
          setIsChatOpen={setIsChatOpen}
        />
        <div className='p-4 border-t border-gray-200 flex items-center space-x-3 gap-2'>
          <button
            onClick={() => setCreateChannelModalActive(true)}
            className='w-12 h-12 bg-blue-500 text-white rounded-full flex items-center justify-center hover:bg-blue-600 transition-all duration-200 shadow-sm cursor-pointer'
            title='Create new chat'>
            <Plus className='w-6 h-6' />
          </button>
          <a
            href='/chat'
            target='_blank'
            rel='noreferrer'
            className='w-12 h-12 bg-gray-100 text-gray-600 rounded-full flex items-center justify-center hover:bg-gray-200 transition-all duration-200 cursor-pointer'
            title='Open in new window'>
            <ExternalLink className='w-5 h-5' />
          </a>
        </div>
      </div>

      {/* Main Chat Area */}
      <div
        className={`flex-1 flex flex-col ${isChatOpen && customActiveChannel && 'chat-main-area-module'
          } `}>
        {
          isChatOpen && customActiveChannel ? (
            <>
              <CustomChannelHeader
                channelId={customActiveChannel}
                channelData={channelData}
                onEditChannel={() => setEditChannelModalActive(true)}
                onViewAttachments={() => {
                  setShowAttachments(!showAttachments)
                  setViewingUserId(null)
                }}
                onViewGroupDetails={() => {
                  setShowGroupDetails(true)
                  setViewingUserId(null)
                }}
                onViewPinnedMessages={() => {
                  setShowPinnedMessages(!showPinnedMessages)
                  setShowAttachments(false)
                  setViewingUserId(null)
                }}
                onCloseChat={() => {
                  setIsChatOpen(false)
                }}
              />
              {showGroupDetails && (
                <GroupDetailsModal
                  channelId={customActiveChannel}
                  channelData={channelData}
                  onClose={() => setShowGroupDetails(false)}
                  profile={profile}
                  onLeaveGroup={handleLeaveGroup}
                  onRemoveMember={handleRemoveMember}
                  onTransferOwnership={handleTransferOwnership}
                  onDeleteGroup={handleDeleteGroup}
                  onViewProfile={handleViewProfile}
                  onAddMembers={onAddMembers}
                  onUpdateGroupName={onUpdateGroupName}
                />
              )}
              {showPinnedMessages ? (
                <PinnedMessagesList
                  channelId={customActiveChannel}
                  onClose={() => setShowPinnedMessages(false)}
                />
              ) : showAttachments ? (
                <AttachmentsList
                  channelId={customActiveChannel}
                  onClose={() => setShowAttachments(false)}
                />
              ) : viewingUserId ? (
                <UserDetailsPanel
                  userId={viewingUserId}
                  onClose={() => setViewingUserId(null)}
                  channelData={channelData}
                  setCustomActiveChannel={setCustomActiveChannel}
                  channels={channels}
                  profile={profile}
                />
              ) : (
                <>
                  <MessageList
                    onReply={handleReply}
                    onAddReaction={handleAddReaction}
                    onContextMenu={handleContextMenu}
                    onViewProfile={handleViewProfile}
                    channelId={customActiveChannel}
                    messages={messages}
                    setMessages={setMessages}
                    loadingMore={loadingMore}
                    hasMore={hasMore}
                    initialLoadComplete={initialLoadComplete}
                    loadMoreMessages={loadMoreMessages}
                    messagesEndRef={messagesEndRef}
                    listRef={listRef}
                    isAtBottom={isAtBottom}
                    setIsAtBottom={setIsAtBottom}
                  />
                  <MessageInput
                    channelId={customActiveChannel}
                    profile={profile}
                    channelData={channelData}
                    replyingTo={replyingTo}
                    onCancelReply={() => setReplyingTo(null)}
                    uploadProgress={uploadProgress}
                    setUploadProgress={setUploadProgress}
                    setMessages={setMessages}
                    setShouldAutoScroll={setShouldAutoScroll}
                  />
                </>
              )}
            </>
          ) : customActiveChannel ? null : null // </div> //   </div> //     </div> //       Choose a conversation from the sidebar to begin //     <div className='text-gray-500'> //     </div> //       Select a chat to start messaging //     <div className='text-xl font-medium text-gray-900 mb-2'> //     </div> //       <Send className='w-10 h-10 text-gray-400' /> //     <div className='w-20 h-20 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-6'> //   <div className='text-center'> // <div className='flex-1 flex items-center justify-center bg-gray-50'> // Show this only when no chat is selected at all // Show empty state when a chat is selected but not open
        }
      </div>

      {/* Context Menu */}
      {contextMenu.isOpen && (
        <div
          className='fixed z-50 bg-white shadow-xl rounded-lg py-2 w-48 border border-gray-200'
          style={{
            top: contextMenu.position.y,
            left: contextMenu.position.x
          }}
          onClick={(e) => e.stopPropagation()}>
          <button
            className='w-full text-left px-4 py-2 hover:bg-gray-50 flex items-center space-x-2 transition-colors cursor-pointer'
            onClick={() => handleContextAction('pin')}>
            <Pin className='w-4 h-4' />
            <span>
              {channelData?.pinnedMessages?.some(
                (pinnedMsg) => pinnedMsg?.messageId === contextMenu?.message.id
              )
                ? 'Unpin message'
                : 'Pin to chat'}
            </span>
          </button>
          {contextMenu.message.senderId === auth.currentUser?.uid && (
            <button
              className='w-full text-left px-4 py-2 hover:bg-gray-50 flex items-center space-x-2 transition-colors cursor-pointer'
              onClick={() => handleContextAction('edit')}>
              <Edit className='w-4 h-4' />
              <span>Edit</span>
            </button>
          )}
          <button
            className='w-full text-left px-4 py-2 hover:bg-gray-50 flex items-center space-x-2 text-red-600 transition-colors cursor-pointer'
            onClick={() => handleContextAction('delete')}>
            <Trash2 className='w-4 h-4' />
            <span>
              {contextMenu.message.senderId === auth.currentUser?.uid &&
                Date.now() -
                (contextMenu.message.createdAt?.seconds
                  ? contextMenu.message.createdAt.seconds * 1000
                  : contextMenu.message.tempTimestamp || Date.now()) <=
                60 * 1000
                ? 'Delete for everyone'
                : 'Remove for me'}
            </span>
          </button>
          <button
            className='w-full text-left px-4 py-2 hover:bg-gray-50 flex items-center space-x-2 transition-colors cursor-pointer'
            onClick={() => {
              handleReply(contextMenu.message)
              setContextMenu({ ...contextMenu, isOpen: false })
            }}>
            <Reply className='w-4 h-4' />
            <span>Reply</span>
          </button>
        </div>
      )}

      {/* User Details Modal */}
      {viewingUser && (
        <UserDetailsModal
          user={viewingUser}
          onClose={() => setViewingUser(null)}
        />
      )}

      {/* Modals */}
      {createChannelModalActive && (
        <ModalDialog
          title='Create a new chat channel'
          active={true}
          onClose={() => setCreateChannelModalActive(false)}>
          <CreateChannelForm
            profile={profile}
            setCustomActiveChannel={setCustomActiveChannel}
            setMessages={setMessages}
            setInitialLoadComplete={setInitialLoadComplete}
            setLastDocument={setLastDocument}
            setNewestMessageTime={setNewestMessageTime}
            setHasMore={setHasMore}
            setShouldAutoScroll={setShouldAutoScroll}
            setIsChatOpen={setIsChatOpen}
            channels={channels}
            onClose={(channelId) => {
              if (channelId) {
                setCustomActiveChannel(channelId)
              }
              setCreateChannelModalActive(false)
            }}
          />
        </ModalDialog>
      )}
      {editChannelModalActive && (
        <ModalDialog
          title='Delete Channel'
          active={true}
          onClose={() => setEditChannelModalActive(false)}>
          <DeleteChannelForm
            channelId={customActiveChannel}
            onClose={() => setEditChannelModalActive(false)}
          />
        </ModalDialog>
      )}
    </div>
  )
}
